/**
 * Shared domain types and enums.
 *
 * Every enum here mirrors a Postgres `check (... in (...))` constraint from
 * docs/tech-spec-v2.md §4 plus the §13 gap resolutions (G1, G4, G5, G7, G14, G17).
 * The `*_VALUES` arrays are the single source of truth: migrations, zod schemas
 * and TypeScript unions are all derived from them, so a value can never drift
 * between the database and the code.
 *
 * NOTE: nothing in this file is a policy number. Caps, delays and safety
 * thresholds live in `policy.ts` (seed values) and, at runtime, in the
 * `quota_policies` / `safety_settings` tables.
 */
import { z } from 'zod'

/** Build a zod enum + TS union from a readonly tuple, keeping one source of truth. */
function enumOf<const T extends readonly [string, ...string[]]>(values: T) {
  return z.enum(values)
}

// ---------------------------------------------------------------------------
// Channels & actions
// ---------------------------------------------------------------------------

/**
 * Channels the platform models. `ENABLED_CHANNELS` below is the subset this build
 * may act on.
 *
 * TIKTOK AND YOUTUBE JOINED 2026-09-19, and joined MODELLED rather than enabled,
 * which is the whole point of there being two lists. An operator has these
 * handles in their files and had nowhere to put them: the import screen offered
 * no column, so a TikTok column landed in `custom_fields` under whatever the
 * spreadsheet happened to call it, and three files spelled it three ways.
 *
 * Modelling one costs nothing and promises nothing. It gives the handle a place
 * to live, one spelling, and a screen that can show it. What it deliberately does
 * NOT do is let anything dispatch: `ENABLED_CHANNELS` is unchanged, so every
 * gate that decides what may be sent still refuses, exactly as it refuses
 * Pinterest and for the same reason. Nothing has been measured on a handset for
 * any of the three, and until it has there is nothing a dispatch could honestly
 * promise to do.
 */
/*
 * THREADS AND MASTODON JOINED 2026-10-15, and youtube was enabled the same day,
 * each with ONE verb, `comment_post` on a post or video named by its link. The
 * engine's walks for all three were measured on handsets as rehearsals (typed,
 * read back, the publish control found, the draft dropped). The publish tap
 * itself is refused in the engine until a person has watched it land once, so
 * enabling them here turns on rehearsals and the approval queue, not posting.
 *
 * REDDIT AND BLUESKY JOINED 2026-10-17 the same way: one verb, `comment_post`
 * under a post named by its link, measured on the Arizona handsets as rehearsals.
 */
export const CHANNELS = ['linkedin', 'email', 'instagram', 'x', 'facebook', 'messenger', 'whatsapp', 'telegram', 'pinterest', 'tiktok', 'youtube', 'threads', 'mastodon', 'reddit', 'bluesky'] as const
export type Channel = (typeof CHANNELS)[number]
export const channelSchema = enumOf(CHANNELS)

/**
 * Channels actually executable in this build (ADR-4/ADR-5).
 *
 * Widened 2026-08-13: `instagram`, `x` and `facebook` joined once every verb
 * on their rows had been measured on the handset (a Pixel 7 Pro driven by the
 * Device Engine, 2026-08-12/13). `facebook` is the platform's name for the
 * channel whose engine app is Messenger. Pinterest stays out: zero measured
 * selectors, so there is nothing a dispatch could honestly promise to do.
 *
 * Widened again 2026-08-14: `whatsapp` joined, its three verbs measured on
 * the same handset (deploy/WHATSAPP.md in the engine repo is the measurement
 * record, including one real send). Unlike the previous three, whatsapp was
 * a NEW value in `CHANNELS`, not a modelled channel waiting at the gate, so
 * the database check constraints that mirror that enum
 * (`channel_accounts_channel_ck`, `prospect_handles_channel_ck`) had to be
 * widened by migration before a single whatsapp row could exist.
 *
 * Widening this array does not by itself turn a channel on, and that is the
 * hazard. The refusal is spread over ten sites and only two of them read this
 * constant, so until the 2026-08-13 pass the rest said `linkedin` by name
 * while this array said otherwise. Each widening walks all ten together, the
 * whatsapp pass included, and the next channel (pinterest, if its selectors
 * are ever measured) has to walk them again:
 *
 *   packages/campaigns/src/validation.ts        reads this array, refuses a method step
 *   packages/executors/src/footprint.ts         reads this array, refuses an entry channel
 *   packages/executors/src/dispatch.ts          decides the engine hand-off, formerly `channel === 'linkedin'`, now the
 *                                               per-account verbs the engine registers
 *   workers/node/src/container.ts               decides the hold TTL, formerly `channel === 'linkedin'`
 *   packages/quota/src/governor.ts              decides min-gap pacing and the single-flight lock, formerly `channel === 'linkedin'`
 *   apps/api/src/routes/accounts.ts             reads this array via enabledChannelSchema, the only insert path for a sending account
 *   packages/executors/src/registry.ts          refuses by absence, since no driver claims the channel
 *   packages/tenancy/src/knowledge/schema.ts    refuses the channel in a brand's rules of engagement
 *   packages/inbox/src/store/pg.ts + checks.ts  mints housekeeping cards, formerly for LinkedIn accounts only
 *   packages/shared/src/policy.ts               no seeded quota row means capDaily 0, which refuses everything
 *
 * That last one is why a channel added here without quota numbers chosen by a
 * person is worse than one left off: it reserves nothing, and the operator is
 * told the person hit a daily ceiling rather than told the channel has no policy.
 */
/*
 * Widened again 2026-08-25: `telegram` joined, with ONE verb. Its driver was
 * measured on the same handset and one real message was sent to a real
 * username. It is the first channel here whose conversation list cannot be
 * read at all -- Telegram's rows publish no name, no preview and no tap
 * target -- so `dm` is not a first instalment with more to follow, it is the
 * whole of what that app can honestly be asked to do from a list-blind
 * driver. `list_conversations` and `inbox_check` are absent for the same
 * reason WhatsApp has no `follow`: not unmeasured, unavailable.
 */
/*
 * Widened again 2026-09-12: `messenger` joined, and this one is a RENAME rather
 * than a new capability. `facebook` had meant the Messenger app,
 * `com.facebook.orca`, since 2026-08-13, because that was the only app in the
 * Facebook family with a measured driver and the platform had one name to give
 * it. The Facebook app itself is now measured -- dm through its own in-app chat
 * sheet, add_friend on a profile, like_post, react_post and profile_view, all
 * proved on a handset -- and it declares a full identity surface, so the two
 * apps can no longer share one name.
 *
 * So `messenger` now means Messenger and `facebook` now means Facebook, which is
 * what both words mean to everybody outside this file. The existing channel
 * accounts, their policy rows, their quota counters and their history all MOVE
 * to `messenger` by migration: nothing about those accounts changed, only the
 * word for them, and leaving them under `facebook` would have silently repointed
 * a live sending account at a different app.
 */
export const ENABLED_CHANNELS = ['linkedin', 'email', 'instagram', 'x', 'facebook', 'messenger', 'whatsapp', 'telegram', 'threads', 'mastodon', 'youtube', 'tiktok', 'reddit', 'bluesky'] as const
export type EnabledChannel = (typeof ENABLED_CHANNELS)[number]
export const enabledChannelSchema = enumOf(ENABLED_CHANNELS)

export function isEnabledChannel(value: Channel): value is EnabledChannel {
  return (ENABLED_CHANNELS as readonly string[]).includes(value)
}

/**
 * Which service sends an email account's mail.
 *
 * Null on every channel a phone performs, where the identity lives on the
 * handset and the provider is not how a performer is chosen.
 *
 * `smtp` is the one that changes what this platform is. Under the other two a
 * vendor holds the mailbox, warms it, paces it and refuses past its own limit.
 * Under `smtp` we open the socket ourselves, so every one of those is ours to
 * do, which is why the governor has to be able to tell them apart rather than
 * treating "email" as one thing.
 *
 * Here in the kernel rather than beside the drivers because two packages need
 * it and neither may import the other: the executors hold the drivers, and the
 * quota governor decides how fast a mailbox may send.
 */
export const EMAIL_PROVIDERS = ['instantly', 'autoklose', 'smtp'] as const
export type EmailProvider = (typeof EMAIL_PROVIDERS)[number]
export const emailProviderSchema = enumOf(EMAIL_PROVIDERS)

/**
 * Does this platform do the sending for that account, rather than ask somebody
 * else to?
 *
 * The question every pacing decision turns on. A vendor mailbox arrives here
 * already paced by the vendor, and pacing it twice only makes it slower. A
 * mailbox we sign into has nothing between it and the provider's own
 * anti-abuse, which does not answer with a rate limit, it answers by taking the
 * address out of service.
 */
export function isSelfSentMailbox(provider: EmailProvider | null | undefined): boolean {
  return provider === 'smtp'
}

/**
 * Full action vocabulary (§5.M7) plus the two human-fed observation actions
 * added by G3 (`inbox_check`) and G5 (`connections_check`), plus the email
 * `send` action used by the Method DSL (§5.M6).
 *
 * This is the union of every app's vocabulary, not any one app's. The same
 * word is a different gesture on a different app, and some apps are missing
 * words the others have, so membership here says only that the platform can
 * name the action. `CHANNEL_ACTION_TYPES` below is what says who has it.
 *
 * The platform resolves, validates, reserves and accounts for all of them.
 * Nothing in this build performs a LinkedIn action against LinkedIn (ADR-4).
 */
export const ACTION_TYPES = [
  'connection_request',
  'dm',
  'inmail',
  'profile_view',
  'follow',
  'like_post',
  'withdraw_request',
  'list_conversations',
  'inbox_check',
  'connections_check',
  'send',
  // ---------------------------------------------------------------------
  // The verbs LinkedIn does not have.
  //
  // Three real, tested capabilities that this vocabulary previously had no
  // word for. When they were named, the apps that perform them were not in
  // `ENABLED_CHANNELS`, so the only way to reach one was an operator asking
  // by hand. The 2026-08-13 channel widening changed that: instagram, x and
  // facebook are enabled now, so a method step on those channels can carry
  // these verbs like any other measured action.
  //
  // Naming a verb is still not enabling a channel. What may be dispatched is
  // decided by `ENABLED_CHANNELS` and by the ten sites listed above it, none
  // of which reads this array. A word for a gesture and a permission to
  // perform it are different things, and this file only ever supplies the
  // first. Pinterest is the standing proof: modelled, unnamed here, refused.
  //
  // `add_friend` is Messenger's connection request. It is a separate verb
  // rather than a second spelling of `connection_request` because it is a
  // different gesture: it renders on the inbox in a people-you-may-know strip
  // rather than on a profile, and the control does not name the person it
  // would add, a sibling node does. Naming it here is what lets the platform
  // stop pretending Messenger speaks LinkedIn.
  'add_friend',
  // `bookmark` is X's silent save, and `save_post` is Instagram's. They are
  // two apps' words for one idea and neither app has the other's word, which
  // is why there is no single `save` here: a shared spelling would let a
  // caller ask X for the verb Instagram has and be told nothing was wrong
  // until the phone refused it.
  //
  // Both are private, on no timeline, notify nobody and are reversible with
  // no trace, which makes them the only writes on those apps that can be
  // exercised end to end without imposing on a person. That is a reason to
  // reach for them first when a slot is being proved, not a reason to treat
  // them as free.
  'bookmark',
  'save_post',
  // `profile_scan` reads a public profile for the words the person put on it:
  // the bio, and where the app renders them, the posts. It is the evidence half
  // of working out what somebody is interested in, and it is a READ in the same
  // arrival-only sense as `profile_view` -- the profile was on screen and these
  // were the words on it, and nothing beyond that is claimed.
  //
  // Separate from `profile_view` rather than an extra field on it because the
  // two answer different questions and one of them is far more expensive to
  // widen later. `profile_view` exists for the DEGREE, which gates whether a dm
  // is allowed; this exists for the words, and a caller who wants one almost
  // never wants the other. Folding them together would also have made every
  // existing profile_view start returning post text, which is a different
  // privacy footprint for a verb people have already approved.
  'profile_scan',
  // `save_contact` puts a person into the handset's own address book, and it
  // is the first verb here that touches no app at all. Measured 2026-08-30:
  // the write goes straight through the Android contacts provider over adb,
  // one insert for the raw contact and one data row each for the name and the
  // number, then a read of the phones column to confirm the number arrived.
  // There is no editor to drive, no Save button to find and no selector to go
  // stale, which is why it is cheap and why it stays that way.
  //
  // It is on every channel a handset drives rather than on one, because the
  // address book belongs to the phone and not to any app on it. That is the
  // whole reason it could not be spelled before: every other verb here is a
  // gesture inside one app, and this one is a gesture on the device those apps
  // happen to sit on. Email is absent for the obvious reason and pinterest for
  // the standing one, that nothing on it was ever measured on a handset.
  //
  // It reaches nobody. Nothing is sent, nothing is published, nobody is
  // notified, and the person whose number it is learns nothing. That is what
  // makes it housekeeping below, and it is also why it is paced anyway: what
  // is local is the write, and what is not local is what the messaging apps do
  // next, which is to send the numbers to their own servers to find out which
  // are registered. A phone that held one contact this morning and holds
  // ninety seven this afternoon produces a single lookup of ninety six
  // strangers, so the writes are spread and the rate is a policy row.
  'save_contact',
  // ---------------------------------------------------------------------
  // Engaging with what somebody actually posted.
  //
  // Both open a named person's LinkedIn profile, walk to their activity, and
  // act on posts that are theirs. That last clause is the whole of why they
  // are new words rather than a wider reading of `like_post`.
  //
  // `like_post` REACTS TO WHATEVER POST IS ON SCREEN. It compares no author,
  // which is why `NEEDS_TARGET` in packages/executors leaves it out, why the
  // engine has never been handed one, and why the 2026-08-13 capability cycle
  // recorded it as unrunnable: aiming it needed a post reference the platform
  // could not produce, and running it unaimed reacts to a stranger. It stays
  // exactly as it is, one reaction to one post a person opens from a card,
  // because changing what an existing verb does is how an approval somebody
  // already gave starts meaning something else.
  //
  // `like_posts` is the aimed version and it is aimed by the WALK rather than
  // by a post reference. The profile is opened by the person's own slug, the
  // activity is reached from that profile, and every post under it is by
  // definition theirs, so the author check the single verb could never do is
  // done by never leaving their page. The plural is the point: this is one
  // visit that leaves several reactions, which is one action, one reservation
  // and one allowance, exactly as a message burst is one send.
  //
  // HOW MANY is not written down here and must not be. It is a band on
  // `safety_settings`, resolved onto the policy snapshot the phone enforces
  // through, and a method step may narrow it further with its own ceiling. A
  // literal here would be the ADR-10 number this platform keeps not writing.
  'like_posts',
  // `comment_post` is the same walk and then one comment on one post.
  //
  // It is the first engagement verb that carries WORDS, which is what makes it
  // a different verb rather than a flag on the one above. Words mean a copy
  // kind, a generator brief, a length rule and a person at the review desk
  // approving them, and a like has none of that to approve. Folding the two
  // together would have produced a verb that sometimes needs approval and
  // sometimes does not, which is the shape nobody can reason about at the
  // moment it matters.
  //
  // Singular, and that is a decision rather than a shortfall. A comment is
  // public and attributed, so three of them under one person's posts in one
  // visit reads as a bot to everybody who scrolls past, and none of the three
  // could be reviewed as the thing it will sit next to. One comment, one
  // draft, one approval.
  'comment_post',
  // `react_post` is NOT a spelling of `like_post` and not a payload of it. The
  // difference is a GESTURE, not an argument: a tap on the reaction control sets
  // the default reaction, and a long press on the same pixels opens a picker
  // whose options are seven different public statements. One control, two acts,
  // so two words.
  //
  // It carries `reaction` and there is no default. A request with no reaction IS
  // a like and should say so by asking for `like_post`, because a default here
  // would make the two verbs silently interchangeable and the quieter of the two
  // is the one that would get chosen by accident.
  //
  // THE OUTCOME NAMES THE REACTION THAT LANDED, not merely that one did. A step
  // that asked for Love and got Like has had a different thing said in public on
  // the account's behalf, and `acted: true` alone cannot tell those apart. The
  // engine refuses to report at all unless two separate controls on the post
  // agree about which reaction is on it.
  //
  // Facebook only, for now, and that is a fact about measurement rather than a
  // decision: it is the one app in the registry whose reaction picker has been
  // dumped and whose seven options have all been set and read back on a handset.
  // LinkedIn has the same gesture and an unmeasured picker.
  'react_post',
  // `repost_post` puts one of the person's posts on THIS account's own timeline.
  //
  // IT PUBLISHES, which is the reason the X row carried no repost until
  // 2026-09-25 and the reason it is a word of its own rather than a flag on the
  // like walk. A like is one tap on somebody else's post. A repost is a post by
  // us, in front of everybody who follows the account, saying this is worth
  // reading, and it stays up until somebody takes it down. So it is aimed the
  // way `comment_post` is, by walking the named person's own posts, and the post
  // is chosen the way a comment's post is, by asking the platform about each one
  // through the same choose endpoint with the intent `repost`. The platform
  // holds that intent to a stricter bar than a comment, from a safety row.
  //
  // Singular, for `comment_post`'s reason and harder: two reposts of one person
  // in one visit reads as a fan account, and each one is our own post. One
  // visit, at most one repost, and none at all when no post clears the bar.
  //
  // No words, so it is not in `COPY_BEARING_ACTIONS`. What is published is the
  // person's post, unchanged, and a quote repost with words of ours on it is a
  // different verb nobody has asked for.
  'repost_post',
  // ---------------------------------------------------------------------
  // The verb that is not about anybody.
  //
  // `idle_browse` opens an app, scrolls the feed, touches nothing and closes.
  // It reaches no person, reads no profile, taps no control and produces no
  // evidence about anyone. It is the only verb here whose entire purpose is to
  // be indistinguishable from what the account's owner does all day.
  //
  // WHY A PLATFORM WOULD SPEND AN ACCOUNT'S TIME ON NOTHING. Every other verb
  // here is outreach, and an account that ONLY does outreach has a shape: it
  // wakes, it sends, it sleeps, and it never once looks at the thing everybody
  // else on the app is there for. The pacing makes each action look human and
  // does nothing about the pattern of the whole day.
  //
  // IT NEEDS NO NEW SELECTOR, which is why it can be listed for every app a
  // handset drives rather than for the ones whose feeds have been walked.
  // Opening an app is measured for every driver in the registry, because every
  // verb starts with it, and scrolling is a device gesture rather than a
  // control. There is nothing here to match and nothing to go stale. That is
  // also the whole of its blast radius: a verb that touches no control cannot
  // press the wrong one.
  //
  // Email is absent for the obvious reason, and pinterest, tiktok and youtube
  // for the standing one: nothing on them was ever measured on a handset, so
  // there is no phone behind an account for an app to be opened on.
  'idle_browse',
  // ---------------------------------------------------------------------
  // The one people search, and it is not a harvest.
  //
  // `company_people_search` looks for ONE person the platform already holds, by
  // the full name on file, on the LinkedIn People tab of the company on file,
  // and it runs only after a send found that person's stored profile link
  // missing or blank. The owner asked for it on 2026-09-27. It answers with a
  // profile only when the name and the company both match exactly, never lists
  // anybody else, and has its own small daily cap because LinkedIn counts it as
  // a search. Nothing schedules it from a workflow: the platform dispatches it
  // itself, once, when a LinkedIn step's link breaks.
  'company_people_search',
  // ---------------------------------------------------------------------
  // The account's OWN post analytics, read once a day (0158).
  //
  // `post_analytics` opens LinkedIn's drawer, its Content analytics and its Top
  // performing posts sorted by engagements, compares each post's lifetime counts
  // with the last read, and on the posts that grew reads who reacted and who
  // commented. The owner asked for it on 2026-10-09. It aims at nobody: the
  // people it finds come back in the result and are filed as prospects under the
  // reservation's brand, the way the Instagram audience read files its rows. It
  // does open the profile of somebody new to read their link off Contact info,
  // which is a profile view they can see, so it has its own small cap. See
  // `post-analytics.ts` and gojiberry-engine src/post_analytics.py.
  'post_analytics',
  // ---------------------------------------------------------------------
  // A NEW POST from an X account (0168).
  //
  // The owner, 2026-10-11: "build verb to post a new tweet with simple text or
  // multimedia on x". `post_tweet` publishes one post on the account's own
  // timeline: words, one picture or clip, or both. It aims at nobody, so it
  // reaches no prospect and needs none, and it is never a campaign step: a person
  // asks for it, one at a time, from the Run an action panel. The phone opens X's
  // composer by the share intent, checks the account named on it and the words
  // in the box, attaches the file through the system photo picker, and presses
  // Post only when the payload says `act: true`. See gojiberry-engine
  // src/x_post.py, which is the other half of this.
  'post_tweet',
  // ---------------------------------------------------------------------
  // One comment under one of the account's OWN posts (0159).
  //
  // The owner, 2026-10-09: "if they have made a comment, we're going to like
  // their comment and we need to reply to their comment as well". The post
  // analytics read finds the comment, and these two act on exactly that one:
  // the engine finds the post again by its key and the comment by its author's
  // name and the start of its words, exactly one, or nothing is touched. Both
  // write in public under the account's name. The engine acts only on a step
  // carrying `act: true`, which the payload builder sets for these two alone.
  'like_comment',
  'reply_comment',
] as const
export type ActionType = (typeof ACTION_TYPES)[number]
export const actionTypeSchema = enumOf(ACTION_TYPES)

/**
 * The verbs LinkedIn's row carries. This is LinkedIn's vocabulary. The
 * `manual_cards_kind_ck` constraint enumerated exactly these until migration
 * 0011 widened it to admit the three device verbs (`add_friend`, `bookmark`,
 * `save_post`) as well, so the card column is thirteen kinds now and the
 * code-side reading of it lives in `CardActionKind` in packages/executors.
 * Adding a verb here still means LinkedIn was measured performing it, nothing
 * less.
 *
 * `inmail` is the one entry the device cannot perform. InMail lives in Sales
 * Navigator, a separate app that is not installed, so the engine's LinkedIn
 * driver registers nine handlers, not ten. It stays here anyway because a
 * human with a Sales Navigator seat can send one by hand, and this array is
 * what lets a manual card name that ask. The line that must hold is narrower
 * than "InMail is impossible": it is "never hand `inmail` to the engine",
 * and dispatch holds it by reading the per-account verb list the engine
 * registers, which never contains `inmail`.
 *
 * An earlier version of this comment claimed the array drove pacing and
 * min-gap grouping. It never did: pacing keys off the channel and the
 * account, and nothing in the quota or pacing code reads this array.
 *
 * `CHANNEL_ACTION_TYPES.linkedin` stopped being a copy of this array on
 * 2026-08-30, and the difference is deliberate. That row now carries
 * `save_contact` as well, which is a write to the handset's address book and
 * not a LinkedIn gesture at all, so it has no business in LinkedIn's own
 * vocabulary. Keeping the two apart is what stops the address-book verb
 * leaking into the places this array still governs: the LinkedIn stub driver
 * claims exactly these, so it never offers to perform a contact write, and the
 * manual card kinds this array is measured against are these plus the device
 * verbs migration 0011 and 0043 admitted, with no card kind for a gesture no
 * card describes.
 *
 * TWELVE SINCE 2026-08-31, and the two that joined were added under a
 * different warrant from every verb here before them, so read this before
 * adding a thirteenth.
 *
 * `like_posts` and `comment_post` are LinkedIn's own gestures, unlike
 * `save_contact`, so this is the array they belong in. What is NOT claimed by
 * putting them here is that a device has been seen performing them. That work
 * is happening in the engine repo as this lands, and membership here hands a
 * verb to no phone: the hand-off in packages/executors requires the account's
 * own engine to have REGISTERED the verb for its slot, so an engine that has
 * not learned them never receives one, and a step carrying one falls through
 * to the registry exactly as `inmail` has always done.
 *
 * Which is why they are here rather than held back until the phone is ready.
 * This array is the card-kind domain, so membership is what gives a person
 * with the handset in their hand something to do in the meantime, and both
 * pass the test that constraint actually applies: a human can open a profile,
 * scroll the activity and tap the reactions, and a human can type a comment.
 * `defaultInstructions` in the stub driver is exhaustive over this array, so
 * neither could be added without words written for the person who has to do
 * it, which is what makes the fallback real rather than nominal.
 *
 * `migrations/0048_linkedin_engagement.sql` widens `manual_cards_kind_ck` to
 * match, and it has to, because `CardActionKind` in packages/executors is
 * derived from this array. Widening the type without the constraint moves the
 * refusal from the compiler to an insert that fails with a reservation already
 * spent, which is the order migration 0011 recorded and 0038 restated.
 */
export const LINKEDIN_ACTION_TYPES = [
  'connection_request',
  'dm',
  'inmail',
  'profile_view',
  'follow',
  'like_post',
  // Beside `like_post` rather than replacing it. One reacts to whatever is on
  // screen and is reachable only by a person holding the phone; this one walks
  // to a named person's activity, where every post is theirs by construction.
  'like_posts',
  'comment_post',
  'withdraw_request',
  'list_conversations',
  'inbox_check',
  'connections_check',
] as const
export type LinkedInActionType = (typeof LINKEDIN_ACTION_TYPES)[number]

/**
 * Which actions each app can perform, measured on a real device rather than
 * designed. This replaces the assumption that the action vocabulary is
 * LinkedIn's everywhere, which is the assumption that would have the platform
 * schedule verbs a phone cannot carry out.
 *
 * READ THIS BEFORE USING IT. A row here says the app has the control. It is
 * NOT permission to dispatch, and no caller may read it as one. What this
 * build may act on is `ENABLED_CHANNELS`, and pinterest is deliberately not
 * in it. The two questions are independent: "does the app have this verb"
 * and "may we use it", and only the second one is a permission. They happen
 * to agree for five of the six apps today, but anything that answers the
 * second with this table has enabled a channel by accident, and the next
 * measured-but-not-yet-enabled app would prove it.
 *
 * Every row was reconciled 2026-08-13 against the engine's per-app handler
 * table, exercised on a Pixel 7 Pro. What was measured, per row:
 *
 * - instagram: `follow` names its target inside the control itself ("Follow
 *   National Geographic", "Following Fahad Anwar"), which is the same safety
 *   property LinkedIn's has, so a mis-tap becomes a mismatch instead of an
 *   action against a stranger. There is no connection request, because a
 *   follow is one-sided and nobody accepts it, which also leaves
 *   `connections_check` with nothing to check. `save_post` is its silent
 *   save. `list_conversations`, `inbox_check` and `profile_view` are all
 *   driven on the device, which this table used to under-report.
 * - facebook: this is Messenger, and `add_friend` is its connection request.
 *   It renders on the inbox rather than on a profile. The two inbox sweeps
 *   are measured here too.
 * - x: `dm`, the two inbox sweeps, and `bookmark`, which is X's silent save.
 *   Its follow control was measured and refused on 2026-08-13. On a followed
 *   profile two nodes read exactly "Following", the button and the following
 *   count label above it, and both move with the length of the bio, so
 *   neither the text nor the position separates them. A selector that
 *   matches one matches the other, and the wrong match silently follows or
 *   unfollows whoever is on screen. `repost` was absent for a different
 *   reason: it publishes.
 *   BOTH JOINED THE ROW ON 2026-09-25, for the owner's X routine (follow them,
 *   like and comment, repost the very strongest post, message them later).
 *   The two findings above did not go away, they moved. Telling the follow
 *   button from the count label is the X engine's measurement to make, and
 *   the row hands the verb to no phone until an engine registers it for the
 *   account's slot. The repost is held on this side instead: it walks the
 *   person's posts like `comment_post`, the platform judges each one with
 *   the intent `repost` against a stricter bar than a comment's, and its
 *   quota row is the tightest on the platform. `repost_post` is on this row
 *   and no other.
 * - whatsapp: `dm`, `list_conversations` and `inbox_check`, measured
 *   2026-08-13 including one real send. The missing verbs are not unmeasured
 *   gaps, they are absent concepts: WhatsApp has no follow, no connection
 *   request and no public post, so the relationship verbs the other rows
 *   carry do not exist there to be asked for, and there is no
 *   `connections_check` because nothing is ever accepted. The address is a
 *   phone number the app resolves itself through its deep link, so the
 *   same-named-stranger failure the other drivers spend selectors defending
 *   against cannot happen. The number's normalisation to digits is a safety
 *   property, not tidying: a malformed number gets the identical "isn't on
 *   WhatsApp" dialog a genuinely absent one gets.
 * - pinterest: nothing measured, so nothing claimed.
 *
 * The two silent saves sit on one row each and not on both, because neither
 * app has the other's verb. Instagram cannot bookmark and X cannot save a
 * post, in the only sense that matters here, which is that the control the
 * engine drives is a different control with a different name on a different
 * screen. Placing each on the one app it was measured on is what keeps
 * `channelPerforms` an answer about a device rather than about a synonym.
 *
 * A channel with an empty row is not a channel with an unknown row. Add a verb
 * here only once somebody has seen it work on a device.
 *
 * One verb here is not an app's verb at all. `save_contact` writes a person
 * into the handset's address book through the Android contacts provider, so it
 * sits on the row of every channel a handset drives and on no other. Read
 * those six entries as "this channel runs on a phone, and a phone has an
 * address book", never as "this app can save a contact". The distinction
 * matters at exactly one place, which is `channelForAction`: it returns null
 * for `save_contact` because six enabled channels perform it, so the channel
 * has to come from the account that is going to do the writing, and that
 * account is what names the phone.
 */
export const CHANNEL_ACTION_TYPES: Record<Channel, readonly ActionType[]> = {
  // The engine measured nine of these on the device. The tenth, `inmail`,
  // stays for the manual card path: see the note on LINKEDIN_ACTION_TYPES.
  //
  // Not a copy of that array since 2026-08-30. `save_contact` is appended
  // here and deliberately kept out of LinkedIn's own vocabulary, because the
  // address book belongs to the phone rather than to LinkedIn. The order is
  // LinkedIn's ten first so the app's verbs still read as a block.
  linkedin: [...LINKEDIN_ACTION_TYPES, 'save_contact', 'idle_browse', 'company_people_search', 'post_analytics', 'like_comment', 'reply_comment'],
  // No `save_contact` here, and it is the one channel where its absence needs
  // saying rather than being obvious. Email is a socket and an API, there is
  // no handset behind an email account and therefore no address book to write
  // to, so the verb has nothing to mean on this row.
  email: ['send'],
  // `profile_scan` sits on the apps whose profile screens were read for it, and
  // nowhere else: Instagram and X, and the Facebook app since 2026-09-14. LinkedIn is the notable absence: its activity feed is
  // behind a tab, so reading it would cost a second screen and break the
  // one-screen bound the verb is built on.
  // `like_posts` and `comment_post` joined on 2026-09-10, and Instagram is the
  // second app to carry them. They are here on the same warrant LinkedIn's
  // entry records: membership in this row hands a verb to no phone, because
  // dispatch reads the per-account verbs the ENGINE registers and an engine
  // that has not learned them never receives one.
  //
  // What is different from LinkedIn's, and what made the measurement worth
  // doing rather than assuming: Instagram publishes the like state ON THE
  // CONTROL. `row_feed_button_like` reads content-desc "Like" before and
  // "Liked" after, and `selected` goes false to true, measured both ways on a
  // real handset on 2026-09-10. LinkedIn's button publishes nothing at all,
  // which is why that driver has to read a reactions summary naming You. So the
  // already-liked gate, which is the gate that decides whether this verb
  // REMOVES a reaction instead of leaving one, is answered more directly here
  // than on the app it was written for.
  //
  // `like_post` is still absent and stays absent. It reacts to whatever post is
  // on screen and compares no author, and on Instagram the screen it would land
  // on is the home feed, which is strangers.
  instagram: ['dm', 'list_conversations', 'inbox_check', 'follow', 'profile_view', 'save_post', 'profile_scan', 'save_contact', 'like_posts', 'comment_post', 'idle_browse'],
  // RENAMED 2026-09-12, and read the row below before this one: `facebook` used
  // to hold exactly what `messenger` now holds, because Messenger was the only
  // measured app in the family. These five verbs did not change, they moved.
  //
  // What `facebook` means now is the Facebook app, com.facebook.katana, and every
  // verb here was proved on a handset on 2026-09-12:
  //
  //   dm            through Facebook's OWN in-app chat sheet. Pressing Message on
  //                 a profile does not hand off to Messenger -- the package never
  //                 changes -- it opens Messenger's chat component embedded in
  //                 this app, on the same inbox. So a message sent here and a
  //                 message sent on the `messenger` row land in the same thread,
  //                 and a sequence that used both would be two touches on one
  //                 conversation rather than two channels.
  //   add_friend    on a profile, which is a different surface from Messenger's
  //                 people-you-may-know strip but the same request.
  //   like_post     one tap, the default reaction.
  //   react_post    the long press and a named choice. See the action's own note.
  //   profile_view  open a person by numeric id and confirm whose profile arrived.
  //
  // `list_conversations` and `inbox_check` are ABSENT and that is deliberate. The
  // Facebook app reaches a conversation through a person's profile, not through a
  // list, so there is no measured inbox here to read. They stay on `messenger`,
  // which is the app that has one.
  //
  // `profile_scan` joined on 2026-09-14, last so the five above still read as the
  // block measured together. It reads the profile `profile_view` already opens by
  // numeric id, whose header fills the first screen and whose posts follow it, from
  // screens captured off the same handset. The row hands the verb to no phone on
  // its own: dispatch reads the verbs each engine registers, so a phone whose
  // engine has not learned the Facebook read never receives one. Messenger stays
  // without it, because the Messenger app has no profile screen at all.
  //
  // `dm` REMOVED 2026-09-20, and the note above it is the reason rather than a
  // contradiction of it. Read that note again: it says a message sent here and a
  // message sent on the `messenger` row "land in the same thread", because what
  // Facebook opens is Messenger's chat component on Messenger's inbox. So this
  // was never a second channel. It was a second door onto the first one, and
  // offering both put a choice in front of somebody where no choice existed:
  // a sequence using each once is two touches on one conversation, which reads
  // to the person as being messaged twice by the same account.
  //
  // Confirmed on the handset 2026-09-20: `fb://messaging` from com.facebook.katana
  // hands straight to com.facebook.orca and lands on ITS account picker, and every
  // person-to-person messaging activity katana ships is a GROUP chat, an event or
  // support. There is no one-to-one inbox in that app to be a channel of.
  //
  // `messenger` keeps the verb, because that is the app that owns the inbox.
  //
  // `like_posts` joined 2026-09-25, after `profile_scan` and before
  // `idle_browse`, which stays last on every handset row. The engine's Facebook
  // driver had declared it since the app was armed and nothing here offered it,
  // so a sequence could only leave one reaction on whatever post was on screen.
  // It walks to the person's own profile by number, like `profile_scan`, and its
  // quota row is LinkedIn's.
  //
  // `comment_post` joined 2026-10-17 at the owner's ask: "start the facebook
  // comment walk to build it and offer it in campaigns and comments as well". It is
  // by link only (COMMENT_BY_LINK_CHANNELS), as on Threads: a step names one post by
  // its link and the post's author, never a person to walk to. Measured on the Pixel 8
  // on 2026-10-19 (the engine's src/facebook_comment.py), where the https link sent to
  // the app's own link handler puts the post on top of the feed. It sits before
  // `idle_browse`, which stays last. The submit is refused on the phone until one
  // comment has been watched landing.
  facebook: ['add_friend', 'like_post', 'react_post', 'profile_view', 'save_contact', 'profile_scan', 'like_posts', 'comment_post', 'idle_browse'],
  // Messenger, com.facebook.orca. These five are the row that was called
  // `facebook` until 2026-09-12 and they are unchanged, verb for verb. The
  // accounts under them are unchanged too: the migration moves the rows, it does
  // not create new ones, because nothing about a Messenger account became
  // different when the word for it did.
  messenger: ['dm', 'list_conversations', 'inbox_check', 'add_friend', 'save_contact', 'idle_browse'],
  // `like_posts` and `comment_post` joined 2026-09-22, the same way Instagram's
  // pair joined on 2026-09-10: the screens were measured on a handset first, the
  // driver was armed, the quota rows were seeded, and only then did the verb
  // appear here. This row is what the sequence builder offers, so a verb absent
  // from it can be armed on the phone, hold a policy, and still be unreachable to
  // anybody building a campaign.
  //
  // `follow` and `repost_post` joined 2026-09-25 for the owner's X routine: follow
  // them straight away, like and comment on a few of their posts, repost the very
  // strongest one, and message them later. The follow note in the doc comment above
  // records why the control was refused on 2026-08-13 (two nodes read "Following"),
  // and that is the engine's to answer, not this row's: membership hands a verb to
  // no phone, because dispatch reads the verbs each engine registers, so a phone
  // whose driver has not learned the follow never receives one. `repost_post` is on
  // this row only, because X is the only app the routine asked it of.
  // `post_tweet` (0168) is X's own new post, aimed at nobody.
  x: ['dm', 'list_conversations', 'inbox_check', 'bookmark', 'profile_scan', 'save_contact', 'like_posts', 'comment_post', 'idle_browse', 'follow', 'repost_post', 'post_tweet'],
  // Exactly three, and that is the lot. The verbs missing from this row are
  // absent concepts rather than unmeasured ones: WhatsApp has no follow, no
  // connection request and no public post, so asking for one is refused
  // before any navigation happens, and there is no connections_check because
  // nothing is ever accepted. Widening this row would claim a control the
  // app does not have.
  //
  // `save_contact` is the exception that proves the rule, and it is the fourth
  // entry rather than a fourth measured verb. Nothing about WhatsApp performs
  // it. It is on this row because a WhatsApp account here lives on a handset,
  // and it is on this row with more consequence than on any other, because
  // WhatsApp is the app that reaches people BY the numbers in that address
  // book. Writing a contact is what makes a person addressable at all here,
  // which is the strongest reason for the pacing and the weakest possible
  // reason to treat the write as free.
  whatsapp: ['dm', 'list_conversations', 'inbox_check', 'save_contact', 'idle_browse'],
  // One verb, and the row is short for a structural reason rather than an
  // unfinished one. Telegram's conversation list publishes nothing a platform
  // could match a person against -- its rows carry no text, no content-desc
  // and no tap target -- so `list_conversations` cannot be written and
  // `inbox_check`, which reaches a thread through that list, has no route.
  // The one verb it does have reaches people by username instead.
  //
  // `save_contact` joins it for the phone's sake, not Telegram's. The row is
  // two long now and still describes one app verb.
  telegram: ['dm', 'save_contact', 'idle_browse'],
  // Still nothing, `save_contact` included. A handset drives this channel in
  // no sense at all, because no selector on it was ever measured, so there is
  // no phone behind a pinterest account for an address book to belong to. An
  // entry here would be the first claim this table ever made about pinterest.
  pinterest: [],
  // One verb since 2026-10-16, the same as the three below: a comment under a
  // video named by its link (gojiberry-engine's perform_tiktok_comment, measured
  // 2026-10-15 on the Bali phone, user 0).
  // TikTok reads its inbox since 2026-10-28, text messages only, read only.
  tiktok: ['comment_post', 'list_conversations', 'inbox_check'],
  // One verb each since 2026-10-15, a comment under a post or video the step
  // names by its link. Nothing else on these rows is measured. The engine refuses
  // the publish tap until a person has watched it land once, so until then a step
  // here is a rehearsal: typed, read back, the publish control found, the draft
  // dropped, and the result says rehearsed. No save_contact: none of the three
  // reaches anybody through an address book.
  youtube: ['comment_post'],
  // Threads and Reddit read their inboxes since 2026-10-28 (gojiberry-engine's
  // drivers, measured on the Pixel 8 that day), read only: no dm on either.
  threads: ['comment_post', 'list_conversations', 'inbox_check'],
  mastodon: ['comment_post'],
  // The same one verb since 2026-10-17 (gojiberry-engine's reddit_comment and
  // bluesky_comment walks, measured on the Arizona handsets as rehearsals).
  reddit: ['comment_post', 'list_conversations', 'inbox_check'],
  // Bluesky reads its chats since 2026-10-29 (measured on the Pixel 8 from the
  // first chat either account had) and answers them the same day, the owner:
  // "Yes, turn it on" (send Bluesky replies from the Inbox and campaigns). A
  // chat in the list opens by name, anybody else on their profile, whose Message
  // button Bluesky draws only when that person takes chats from the account.
  bluesky: ['comment_post', 'list_conversations', 'inbox_check', 'dm'],
}

/**
 * Actions an author may put on a channel that the channel's own app does not
 * perform, because another app carries them. One entry, from the owner's "send on
 * Messenger and Facebook": a message on Facebook is sent through Messenger, whose
 * chat is what Facebook's own Message control opens, so the campaign program holds
 * it as a Messenger message (see `stepChannelFor` in @gojibasel/campaigns). Nothing
 * here is metered on the channel it is listed under.
 */
export const ROUTED_ACTIONS: Partial<Record<Channel, readonly ActionType[]>> = {
  facebook: ['dm'],
}

/** Whether an author may put this action on this channel, routed actions included. */
export function channelOffersAction(channel: Channel, action: ActionType): boolean {
  return CHANNEL_ACTION_TYPES[channel].includes(action) || (ROUTED_ACTIONS[channel]?.includes(action) ?? false)
}

/**
 * The verbs that put WORDS in front of a person.
 *
 * Everything else in `ACTION_TYPES` is a gesture. A profile view arrives and
 * reads, a like taps a control, a follow presses a button, and none of them
 * carries a sentence anybody wrote. Five verbs do, and the split matters
 * wherever the platform asks somebody to write, review or approve copy: a
 * screen that offers a brief for a profile view is asking what tone to use for
 * looking at somebody, which is not a question.
 *
 * WRITTEN DOWN HERE BECAUSE IT WAS NOWHERE. The knowledge existed in three
 * places and none of them could answer it. The DSL lets `content: "generated"`
 * sit on a `profile_view` and validates it happily, which means a model call
 * would be spent writing copy for a step that can never say it.
 * `OUTREACH_ACTIONS` in the campaigns validator is a narrower idea, the verbs
 * that reach a stranger, and deliberately omits `send`. `copyChannelFor` maps a
 * step to a copy kind but falls through to `linkedin_dm`, so it answers for a
 * profile view rather than refusing, and its own comment says it is reached
 * "only by its remaining copy-bearing action", which is a precondition it
 * cannot check. So this is the list those three were each assuming.
 *
 * A NEW COPY-BEARING VERB BELONGS HERE ON THE DAY IT LANDS. `comment_post` is
 * the standing example of the cost of the other order: it arrived on
 * 2026-08-31 and every place that had hardcoded "linkedin means a note or a
 * DM" was wrong for as long as it took somebody to notice.
 */
export const COPY_BEARING_ACTIONS: readonly ActionType[] = [
  'send',
  'dm',
  'inmail',
  'connection_request',
  'comment_post',
  // A reply under their comment on one of our own posts (0159). Its words are
  // written for that comment and, on LinkedIn, always reviewed before they go.
  'reply_comment',
  // A new post on X (0168). Its words go on the account's own timeline, read by
  // everybody who follows it, so they are copy in the fullest sense.
  'post_tweet',
]

/**
 * Does this verb say anything? False for a gesture, and false for an unknown or
 * absent action, because a step that names no verb sends no words either.
 */
export function carriesCopy(action: string | null | undefined): boolean {
  return action != null && (COPY_BEARING_ACTIONS as readonly string[]).includes(action)
}

/**
 * Can this app perform this action? Capability only. A `true` here is never a
 * reason to dispatch: `isEnabledChannel` answers that, and the engine hand-off
 * narrows further still, to the verbs the engine registered for the account's
 * slot. `inmail` is the standing example of the gap: true here, and never on
 * any slot's registered verbs.
 */
export function channelPerforms(channel: Channel, action: ActionType): boolean {
  return CHANNEL_ACTION_TYPES[channel].includes(action)
}

/**
 * Housekeeping actions exempt from `max_open_cards` (G13) — they are how the
 * human feeds the platform reply/connection facts, so they must never be
 * starved by a backlog of outreach cards.
 *
 * `save_contact` joined on 2026-08-30 and it is the first member that is not a
 * sweep. What the three sweeps and the contact write have in common is the
 * only thing this array has ever actually meant: none of them reaches a
 * person, so none of them is outreach, so none of them may be metered as if it
 * were. Read what membership buys, because three of the four are far from
 * obvious and one of them costs something:
 *
 *   - Exempt from `max_open_cards`. Moot for this verb, which never becomes a
 *     card, and harmless.
 *   - Exempt from the operator's hourly action cap in the quota governor. This
 *     is the one that decides whether the feature works. Saving a list of a
 *     hundred people is a hundred operator-origin actions on one identity, and
 *     without the exemption the first handful would spend the whole hour's
 *     budget for actually reaching anybody.
 *   - Exempt from auto-freeze on the weekly ceiling. Right by construction: a
 *     verb that reaches nobody cannot be the thing that got an account
 *     flagged, and freezing an account over its address book would take real
 *     outreach offline for a write no platform ever saw.
 *   - NOT paced by the governor, and this is the cost. `packages/quota` builds
 *     the min-gap from `(DEVICE_CHANNELS.has(channel) && !housekeeping)`, so a
 *     housekeeping verb neither reads nor stamps the account's pace clock, and
 *     the `min_gap_minutes` on its policy row is not enforced there.
 *
 * That last point is deliberate rather than an oversight, because the pace
 * clock is keyed on the ACCOUNT and not on the (account, action): one clock,
 * shared by every verb on the phone. Pacing contact writes through it would
 * mean each write stamped the same clock a DM reads, so a run of contact
 * writes every couple of minutes would refuse every outreach message on that
 * account for as long as it lasted. A verb that reaches nobody must not be
 * able to starve the verbs that do.
 *
 * So the gap on the `save_contact` policy row is a real number that the
 * governor does not apply, and whatever issues the writes has to space them by
 * reading that row. It is still a policy row and never a literal (ADR-10), and
 * the number is in one place. Anyone wiring the write path should start here.
 */
export const HOUSEKEEPING_ACTION_TYPES = ['inbox_check', 'connections_check', 'list_conversations', 'save_contact'] as const
export type HousekeepingActionType = (typeof HOUSEKEEPING_ACTION_TYPES)[number]

export function isHousekeepingAction(action: ActionType): action is HousekeepingActionType {
  return (HOUSEKEEPING_ACTION_TYPES as readonly string[]).includes(action)
}

/**
 * The verbs a person asks for that are about the account itself and reach no
 * other person (0168). A new post goes on the account's own timeline, so there
 * is no prospect to name and no suppression entry that could apply to it.
 *
 * Not housekeeping, and the difference matters: housekeeping is unpaced and
 * exempt from the operator's hourly cap, and a public post is neither. It keeps
 * its quota row, its gap and the hourly cap. The only thing this set changes is
 * that a request for one is not refused for naming nobody.
 */
export const OWN_ACCOUNT_ACTION_TYPES = ['post_tweet'] as const

export function isOwnAccountAction(action: ActionType): boolean {
  return (OWN_ACCOUNT_ACTION_TYPES as readonly string[]).includes(action)
}

/**
 * The channels whose comment names the POST by its link rather than a person
 * (2026-10-15). On Threads, Mastodon and YouTube the engine opens the link, proves
 * it arrived at the post the link names, and replies there. It never walks to a
 * person's profile, so it needs no handle and no name, and a person on file is
 * optional: when one is given they are checked for suppression like any other.
 */
// Facebook joined on 2026-10-19: the engine's Facebook walk is by link only, measured
// on the Pixel 8. Walking to a person's own posts on Facebook was never measured.
export const COMMENT_BY_LINK_CHANNELS = ['threads', 'mastodon', 'youtube', 'tiktok', 'reddit', 'bluesky', 'facebook'] as const

/**
 * The channels whose comment walks to a PERSON by default (a campaign step picks
 * one of their own recent posts), and names one post by its link only when the
 * request asks in as many words, with `byLink: true` in its extras. A scheduled
 * comment from Prospector (2026-10-16) is the one caller that asks: it names the
 * post somebody chose, never a person. Keep in step with the engine, which
 * routes a comment carrying `byLink` to its open-the-link routine.
 */
export const COMMENT_BY_LINK_WHEN_ASKED_CHANNELS = ['x', 'linkedin', 'instagram'] as const

/** The extras key that asks for a comment by link on the channels above. */
export const COMMENT_BY_LINK_EXTRA = 'byLink'

export function commentsByLink(
  channel: Channel,
  action: ActionType,
  extras?: Readonly<Record<string, unknown>> | null,
): boolean {
  if (action !== 'comment_post') return false
  if ((COMMENT_BY_LINK_CHANNELS as readonly string[]).includes(channel)) return true
  return extras?.[COMMENT_BY_LINK_EXTRA] === true && (COMMENT_BY_LINK_WHEN_ASKED_CHANNELS as readonly string[]).includes(channel)
}

/** Can a comment on this channel name its post by link, when asked to. */
export function canCommentByLink(channel: Channel): boolean {
  return (
    (COMMENT_BY_LINK_CHANNELS as readonly string[]).includes(channel) ||
    (COMMENT_BY_LINK_WHEN_ASKED_CHANNELS as readonly string[]).includes(channel)
  )
}

/** The extras key the post link travels under, per channel, as the engine reads it. */
export function commentLinkKey(channel: Channel): 'postUrl' | 'videoUrl' {
  return channel === 'youtube' || channel === 'tiktok' ? 'videoUrl' : 'postUrl'
}

/**
 * What is wrong with a link-targeted comment's link, or null when it is usable.
 *
 * The engine parses the link itself and refuses one it cannot read, but only after
 * the platform has reserved the action and a phone has woken for it, so the shape
 * is checked here first. The rules mirror the engine's parsers: a Threads post is
 * threads.com (or threads.net) /@handle/post/code, a Mastodon post is
 * https://server/@user/id or /@user@home/id, and a YouTube video is a watch link,
 * a youtu.be link or a Short. YouTube's watch page shows no video id, so a YouTube
 * comment also names the title or the channel it expects to find there. A Facebook
 * post (2026-10-17) is a /posts/ or /videos/ link under a page or a person, a group
 * post, a permalink.php or photo link with its id, or a share/p link, on
 * facebook.com, m.facebook.com or web.facebook.com.
 */
export function commentLinkProblem(
  channel: Channel,
  extras: Readonly<Record<string, unknown>>,
): { path: string; message: string } | null {
  const key = commentLinkKey(channel)
  const raw = extras[key] ?? (channel === 'youtube' ? extras['postUrl'] : undefined)
  const link = typeof raw === 'string' ? raw.trim() : ''
  if (link === '') return { path: `payload.${key}`, message: channel === 'youtube' ? 'paste the link to the video' : 'paste the link to the post' }
  let url: URL
  try {
    url = new URL(link)
  } catch {
    return { path: `payload.${key}`, message: 'that is not a link' }
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return { path: `payload.${key}`, message: 'that is not a web link' }
  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  const path = url.pathname
  if (channel === 'threads') {
    if (host !== 'threads.com' && host !== 'threads.net') return { path: `payload.${key}`, message: 'a Threads post link starts with threads.com' }
    if (!/^\/@[^/]+\/post\/[^/]+\/?$/.test(path)) return { path: `payload.${key}`, message: 'a Threads post link looks like threads.com/@name/post/...' }
    return null
  }
  if (channel === 'mastodon') {
    if (!/^\/@[^/]+\/\d+\/?$/.test(path)) return { path: `payload.${key}`, message: 'a Mastodon post link looks like https://server/@name/123456' }
    return null
  }
  if (channel === 'reddit') {
    // A post's own link. A share link (/r/name/s/code) only redirects, and the
    // engine proves arrival by the post id, so it is refused rather than guessed.
    if (host !== 'reddit.com' && !host.endsWith('.reddit.com')) return { path: `payload.${key}`, message: 'a Reddit post link starts with reddit.com' }
    if (!/^\/r\/[A-Za-z0-9_]{2,21}\/comments\/[a-z0-9]{4,10}(\/[^/]*)?\/?$/.test(path)) {
      return { path: `payload.${key}`, message: 'a Reddit post link looks like reddit.com/r/name/comments/abc123/...' }
    }
    return null
  }
  if (channel === 'bluesky') {
    if (host !== 'bsky.app') return { path: `payload.${key}`, message: 'a Bluesky post link starts with bsky.app' }
    if (!/^\/profile\/[^/]+\/post\/[a-z0-9]{8,20}\/?$/.test(path)) return { path: `payload.${key}`, message: 'a Bluesky post link looks like bsky.app/profile/name.bsky.social/post/...' }
    return null
  }
  if (channel === 'tiktok') {
    // The engine opens these straight into the video. A short link (vm. or vt.) has
    // not been measured live, so it also needs the author the phone checks for.
    const full = host === 'tiktok.com' || host === 'm.tiktok.com'
    if (full && /^\/@[A-Za-z0-9._]{2,24}\/video\/\d+\/?$/.test(path)) return null
    if (host === 'vm.tiktok.com' || host === 'vt.tiktok.com') {
      const handle = extras['expectHandle']
      if (typeof handle === 'string' && handle.trim() !== '') return null
      return { path: 'payload.expectHandle', message: 'a short TikTok link needs the author it should open, because the phone checks it' }
    }
    return { path: `payload.${key}`, message: 'a TikTok video link looks like tiktok.com/@name/video/123456' }
  }
  if (channel === 'x') {
    if (!['x.com', 'twitter.com', 'mobile.twitter.com', 'mobile.x.com'].includes(host)) return { path: `payload.${key}`, message: 'an X post link starts with x.com' }
    if (!/^\/[A-Za-z0-9_]{1,15}\/status\/\d+\/?$/.test(path)) return { path: `payload.${key}`, message: 'an X post link looks like x.com/name/status/123456' }
    return null
  }
  if (channel === 'linkedin') {
    if (host !== 'linkedin.com' && !host.endsWith('.linkedin.com')) return { path: `payload.${key}`, message: 'a LinkedIn post link starts with linkedin.com' }
    if (!/^\/(posts\/[^/]+|feed\/update\/urn:li:(activity|share|ugcPost):\d+)\/?$/.test(path)) {
      return { path: `payload.${key}`, message: 'a LinkedIn post link looks like linkedin.com/posts/... or linkedin.com/feed/update/urn:li:activity:...' }
    }
    return null
  }
  if (channel === 'instagram') {
    if (host !== 'instagram.com') return { path: `payload.${key}`, message: 'an Instagram post link starts with instagram.com' }
    if (!/^\/(p|reel|reels)\/[A-Za-z0-9_-]+\/?$/.test(path)) return { path: `payload.${key}`, message: 'an Instagram post link looks like instagram.com/p/... or instagram.com/reel/...' }
    return null
  }
  if (channel === 'facebook') {
    // One post, never a person, a page or a search. A profile, a page's front and
    // a search all open somewhere with many posts on it, and the phone would have
    // no way to know which one was meant.
    if (host !== 'facebook.com' && host !== 'm.facebook.com' && host !== 'web.facebook.com') {
      return { path: `payload.${key}`, message: 'a Facebook post link starts with facebook.com' }
    }
    const params = url.searchParams
    const filled = (name: string): boolean => /^[A-Za-z0-9]+$/.test(params.get(name) ?? '')
    const isPost = /^\/[^/]+\/(posts|videos)\/[A-Za-z0-9]+\/?$/.test(path) && !/^\/(groups|search|share)\//.test(path)
    const isGroupPost = /^\/groups\/[^/]+\/posts\/[A-Za-z0-9]+\/?$/.test(path)
    const isPermalink = path === '/permalink.php' && filled('story_fbid') && filled('id')
    const isPhoto = /^\/photo(\.php)?\/?$/.test(path) && filled('fbid')
    const isShare = /^\/share\/p\/[A-Za-z0-9]+\/?$/.test(path)
    if (!isPost && !isGroupPost && !isPermalink && !isPhoto && !isShare) {
      return { path: `payload.${key}`, message: 'a Facebook post link looks like facebook.com/name/posts/..., facebook.com/permalink.php?story_fbid=... or facebook.com/share/p/...' }
    }
    return null
  }
  if (channel === 'youtube') {
    const isWatch = (host === 'youtube.com' || host === 'm.youtube.com') && path === '/watch' && (url.searchParams.get('v') ?? '') !== ''
    const isShort = (host === 'youtube.com' || host === 'm.youtube.com') && /^\/(shorts|live|embed)\/[^/]+\/?$/.test(path)
    const isShortLink = host === 'youtu.be' && /^\/[^/]+\/?$/.test(path)
    if (!isWatch && !isShort && !isShortLink) return { path: `payload.${key}`, message: 'a YouTube link looks like youtube.com/watch?v=..., youtu.be/... or youtube.com/shorts/...' }
    const said = (value: unknown): boolean => typeof value === 'string' && value.trim() !== ''
    if (!said(extras['expectTitle']) && !said(extras['expectChannel'])) {
      return { path: 'payload.expectTitle', message: 'name the video title or the channel, because the phone checks one of them before it comments' }
    }
    return null
  }
  return null
}

/**
 * The one enabled channel that performs this action, or null when there is no
 * honest answer.
 *
 * This used to read "email for `send`, LinkedIn for everything else", which is
 * the LinkedIn-shaped global enum in the form of a function: asked about a verb
 * LinkedIn does not have, it answered LinkedIn anyway, and a caller that pushed
 * that into a quota lookup would pace and cap another app's action against
 * LinkedIn's numbers. It is derived from `CHANNEL_ACTION_TYPES` now, narrowed
 * to the channels this build may act on, and it returns null rather than guess.
 *
 * null means "the action alone does not say", not "LinkedIn". Since the
 * 2026-08-13 channel widening the null cases are the shared verbs: `dm`,
 * `list_conversations` and `inbox_check` are real verbs on five apps now
 * that whatsapp carries all three, and `follow` and `profile_view` on two,
 * so anything that needs a channel for
 * routing, pacing or a quota row must take it from the step or the account,
 * which is where it is already carried. A caller must never read the null as
 * "pick LinkedIn's numbers".
 *
 * The single-app verbs resolve: `send` to email, `add_friend` to facebook,
 * `bookmark` to x, `save_post` to instagram, and the five verbs only LinkedIn
 * has to linkedin. Those three device verbs were the null cases before the
 * widening, when no enabled channel had them, and their answers flipped the
 * moment their apps were enabled. That is this function working as designed,
 * not a regression.
 *
 * `save_contact` is the widest null of all, on six channels, and it is the one
 * case where the null is the whole answer rather than a shortfall. The action
 * says nothing about which channel because it is not about a channel: the
 * address book belongs to the phone. What a caller needs is the account, which
 * names the handset the write lands on, and no amount of looking at the verb
 * will produce it.
 */
export function channelForAction(action: ActionType): EnabledChannel | null {
  const performers = ENABLED_CHANNELS.filter((channel) => channelPerforms(channel, action))
  return performers.length === 1 ? (performers[0] ?? null) : null
}

/**
 * Does this action go out as a burst of messages in one thread visit, rather
 * than as one message?
 *
 * True for the enabled channels a handset types into, and only for `dm`.
 * Derived from `ENABLED_CHANNELS` rather than written out, so the next channel
 * measured on a phone is burst-capable the day it is enabled and nobody has to
 * remember a second list. The reason is structural rather than per-app: what
 * makes a burst possible is a thread and a composer somebody types into, and
 * every app on that array except email has both.
 *
 * **Email is refused deliberately, not by omission.** Nothing types an email.
 * The body goes into a MIME part, through an API or down an SMTP socket, and a
 * newline in it is a line break inside a paragraph exactly as it has always
 * been. Reading it as a boundary here would quietly chop every email the
 * platform sends into a stack of one-line messages, which is not a burst, it is
 * a broken email.
 *
 * **`dm` and nothing else, also deliberately.** A burst is several messages in
 * one thread, and a thread is the only place that shape exists. A connection
 * request note is one field submitted with the request itself, so there is no
 * second message to send and a line break in it stays what it always was, a
 * refusal. Every other verb on these channels carries no text at all.
 *
 * A `false` here is not permission's opposite. It says the action sends one
 * message, and one message is still what almost everything sends.
 */
export function channelSendsBursts(channel: Channel, action: ActionType): boolean {
  if (action !== 'dm') return false
  // `channelOffersAction` as well since 2026-10-15: Threads, Mastodon and YouTube
  // are enabled handset channels with no thread and no `dm` at all, so "enabled
  // and not email" stopped being the same statement as "has a composer to burst
  // in". Offers rather than performs, because Facebook's `dm` is routed through
  // Messenger's composer and bursts there.
  return ENABLED_CHANNELS.some((enabled) => enabled === channel && enabled !== 'email') && channelOffersAction(channel, 'dm')
}

// ---------------------------------------------------------------------------
// Reservations (G1) & dispatch (G10)
// ---------------------------------------------------------------------------

/** G1 — reservations are durable Postgres rows with this lifecycle. */
export const RESERVATION_STATES = ['reserved', 'dispatched', 'committed', 'released', 'revoked'] as const
export type ReservationState = (typeof RESERVATION_STATES)[number]
export const reservationStateSchema = enumOf(RESERVATION_STATES)

/** States that still hold a counter increment; only these may be released/revoked (G1 rule 1). */
export const OPEN_RESERVATION_STATES = ['reserved', 'dispatched'] as const
export type OpenReservationState = (typeof OPEN_RESERVATION_STATES)[number]

export function isOpenReservation(state: ReservationState): state is OpenReservationState {
  return state === 'reserved' || state === 'dispatched'
}

/** G1/G10 — where a reserved action was actually sent. Recorded at dispatch, never changed. */
export const DISPATCH_TARGETS = ['email_instantly', 'manual_card', 'engine', 'dry_run'] as const
export type DispatchTarget = (typeof DISPATCH_TARGETS)[number]
export const dispatchTargetSchema = enumOf(DISPATCH_TARGETS)

/** Why a reservation stopped holding its counters. Written to `reservations.release_reason`. */
export const RELEASE_REASONS = [
  'hold_expired',
  'driver_failure',
  'executor_crash',
  'card_skipped',
  'card_expired',
  'suppressed_at_commit',
  'enrollment_paused',
  'policy_changed',
  'kill_switch',
  'admin_revoked',
  'engine_fatal',
  // An action was queued for the Device Engine but no engine session is
  // listening for the account, so the custodian gives the allowance back
  // immediately rather than letting the hold expire.
  'no_engine_registered',
  // The channel's own app resolved the person's address and answered that no
  // account exists there (WhatsApp's "isn't on WhatsApp" dialog, measured).
  // A fact about the person on that one channel, never a device fault: the
  // allowance goes back because nothing was received, and the executors
  // package refuses any re-dispatch of the person on that channel. Kept
  // distinct from driver_failure because the operator responses are opposites:
  // one says look at the phone, this says stop asking this app about this
  // person.
  'target_not_on_platform',
  // The step's condition turned out to be false at dispatch, so the method took
  // the arm it declares for that and this step was never performed. Nothing was
  // spent and nothing went wrong, which is exactly why it is not
  // `driver_failure`: that reason is read by a human as "go and look at the
  // device", and every hour spent looking at a device over a method correctly
  // taking its else arm is an hour wasted. A campaign that gates half its steps
  // produces a great many of these, so the reason they carry has to be the one
  // that means "as written".
  'condition_not_met',
  // Somebody pressed Stop all sending while this was queued, or a phone asked
  // for it after the stop. Kept apart from `kill_switch`, which is one account
  // switched off: this is a platform, a brand or an account stopped on purpose
  // and expected to start again, so the work it pulled back is work to send
  // again later rather than work that should never have been asked for.
  'sending_halted',
  // The phone opened the profile and the name on it was not the name this
  // platform holds for the person, so it sent nothing. A fact about the record,
  // never about the device, which is why it is not `engine_fatal`: the operator
  // response is to look at the name, and nothing was typed, so the step may be
  // sent again once the name is right.
  'name_mismatch',
  // The phone tapped Message and LinkedIn offered no InMail: the Premium upsell,
  // no credits left, or a person who is already a connection. Nothing was typed.
  'inmail_unavailable',
  // A message to a company page, and the page has no Message button or does
  // not take messages. Nothing was typed, so the allowance goes back and the
  // step settles as could not send. A fact about the page, not the device.
  'company_messaging_off',
  // Instagram search showed nobody who was this person, so the phone opened
  // nothing past the Accounts list and touched nothing (0139). A fact about the
  // record, like a name mismatch: the person is flagged in Approvals with the
  // accounts the phone saw, to pick one or paste the username.
  'search_no_match',
  // The phone walked the whole step and stopped short of the one tap that
  // publishes, because the step did not say act (2026-10-15, the comment walks on
  // Threads, Mastodon and YouTube). The words were typed, read back and thrown
  // away, the publish control was found, and nothing went out. Nothing went wrong
  // either, so it is not `engine_fatal`, which sends a person to look at a phone.
  'rehearsed_on_device',
] as const
export type ReleaseReason = (typeof RELEASE_REASONS)[number]
export const releaseReasonSchema = enumOf(RELEASE_REASONS)

/** Result transport from any executor / the Device Engine (§5.M7, G10). */
export const EXECUTION_STATUSES = ['ok', 'retryable', 'fatal'] as const
export type ExecutionStatus = (typeof EXECUTION_STATUSES)[number]
export const executionStatusSchema = enumOf(EXECUTION_STATUSES)

/**
 * What a dispatch attempt means to the scheduler. The three execution statuses
 * all describe a settled attempt; `handed_off` is the fourth possibility a
 * dispatch has: the action now belongs to a manual card or to the Device
 * Engine, the reservation stays `dispatched`, and settlement arrives later
 * through the card disposition or the engine result report. Committing a
 * hand-off at dispatch time closed the reservation before the engine could
 * claim it and made the card-skip release throw, so a hand-off must never be
 * read as `ok`.
 *
 * `else_branch` is the fifth, and the only one that is not about whether the
 * attempt worked. It says the step's condition is false, so the method takes
 * the arm it already declares for that (`skip`, `end` or `goto:<i>`) and this
 * step is simply not performed.
 *
 * It exists because there was previously nothing to say it with, and both
 * substitutes were wrong in a way that reached real campaigns. Read as `fatal`,
 * a rule table doing precisely its job -- the table documented as the one that
 * never blocks an enrollment -- failed the step and paused the enrollment for a
 * human. Read as `retryable`, the scheduler put the step back to pending and
 * came round every lease interval for the life of the enrollment, because a
 * retry is the one thing that never takes an else arm: nothing about "this
 * person is not a first-degree connection" changes by asking the same step
 * again, and the arm that does change it was never reached. That failure is
 * silent, which makes it worse than the loud one it replaced. The vocabulary
 * needed a fifth word, so here it is.
 *
 * `goal_held` is the sixth, and it is `else_branch`'s mirror. The step is not
 * performed either, but because what it wants is ALREADY true: a connection
 * request to somebody the account already invited. The step settles as skipped
 * with the reason and the method carries on down its main path, because the
 * person is exactly where the step would have put them. Read as `fatal` it
 * failed the step and parked the enrollment for a human over an invitation that
 * was doing its job, which is what the live test campaign met on 2026-09-28.
 */
export const DISPATCH_STATUSES = [...EXECUTION_STATUSES, 'handed_off', 'else_branch', 'goal_held'] as const
export type DispatchStatus = (typeof DISPATCH_STATUSES)[number]
export const dispatchStatusSchema = enumOf(DISPATCH_STATUSES)

// ---------------------------------------------------------------------------
// Channel accounts (G4, G11)
// ---------------------------------------------------------------------------

/** G4 — admin-only kill switch column. */
export const ACCOUNT_ADMIN_STATES = ['enabled', 'disabled'] as const
export type AccountAdminState = (typeof ACCOUNT_ADMIN_STATES)[number]
export const accountAdminStateSchema = enumOf(ACCOUNT_ADMIN_STATES)

/** G4 — system/engine/admin-writable observed health. */
export const ACCOUNT_OBSERVED_STATES = ['warming', 'healthy', 'throttled', 'flagged'] as const
export type AccountObservedState = (typeof ACCOUNT_OBSERVED_STATES)[number]
export const accountObservedStateSchema = enumOf(ACCOUNT_OBSERVED_STATES)

/**
 * Why `observed_state` is what it is, from a closed set the PLATFORM writes.
 *
 * `admin_state_reason` records why a person moved the kill switch. Until this
 * existed nothing recorded why the system moved observed health, so a flag
 * raised by ten failed taps on a handset with a flat battery was
 * indistinguishable from a flag raised because the platform restricted the
 * account. Those two want opposite treatment, which is the whole point of the
 * column.
 *
 * CLOSED, AND NEVER SUPPLIED BY A CALLER. The engine reports its own free-text
 * reason when it degrades an account, and that text goes on the audit row where
 * it always went. It must never reach this column: an engine that could write
 * `failure_circuit_breaker` here could clear its own flag on the next sweep,
 * which is the one thing the recovery path must not allow.
 *
 *   failure_circuit_breaker  consecutive failures tripped the governor's breaker
 *   weekly_cap_threshold     the account is near its weekly ceiling
 *   engine_report            a handset reported worse health for this account
 *   admin                    a person set this state from the console
 *   halt_ladder_exhausted    the breaker tripped again after every timed halt
 *                            on the ladder, and only a person clears that
 *
 * Null means no rule is holding the account back, which is every row that
 * predates the column and every account nothing has flagged.
 */
export const ACCOUNT_OBSERVED_STATE_REASONS = [
  'failure_circuit_breaker',
  'weekly_cap_threshold',
  'engine_report',
  'admin',
  'halt_ladder_exhausted',
  'identity_unreadable',
  /** A walk of every active phone found it on none (docs/ACCOUNT-POLICY.md rule 4). Clears itself when a walk finds it. */
  'missing_from_devices',
] as const
export type AccountObservedStateReason = (typeof ACCOUNT_OBSERVED_STATE_REASONS)[number]

/**
 * The one reason a flag clears itself, and the only value that unlocks recovery.
 *
 * Exported as a constant rather than written out at each site so the writer,
 * the reader and the conditional update that performs the clear cannot drift
 * apart into a flag nothing can ever take back.
 */
export const BREAKER_FLAG_REASON: AccountObservedStateReason = 'failure_circuit_breaker'

/**
 * A phone that could not read which account its app is signed in as. Nothing
 * happened in front of the platform: the app was fine and the engine refused to
 * guess. So clearing it does not restart the warm-up quiet days, the same as the
 * breaker's flag (owner, 25 Sep, Agnes's LinkedIn locked twice by this). It does
 * not clear itself, a person still takes it off.
 */
export const IDENTITY_UNREADABLE_FLAG_REASON: AccountObservedStateReason = 'identity_unreadable'

/** The only automatic flag since 2026-10-25: no phone holds the account. A walk that finds it takes it back. */
export const MISSING_FLAG_REASON: AccountObservedStateReason = 'missing_from_devices'

/** Flags whose clearing costs no quiet days, because the platform saw nothing. */
export const QUIET_DAYS_EXEMPT_FLAG_REASONS: readonly AccountObservedStateReason[] = [
  BREAKER_FLAG_REASON,
  IDENTITY_UNREADABLE_FLAG_REASON,
  MISSING_FLAG_REASON,
]

/** The reason to record for an engine health report, from the report's own words. */
export function engineFlagReason(detail: string | null | undefined): AccountObservedStateReason {
  return typeof detail === 'string' && /^\s*IdentityUnreadable(Standing)?\b/.test(detail) ? IDENTITY_UNREADABLE_FLAG_REASON : 'engine_report'
}

/**
 * The flag the breaker raises when its halt ladder has run out, and why it is
 * not `BREAKER_FLAG_REASON`.
 *
 * A breaker flag clears itself when the handset proves the right account is
 * signed in again, because a flat battery is the commonest way to fail ten
 * times in a row and a charger is its whole answer. By the time the ladder has
 * run out the account has been paused and let back four times across more
 * than a day and has failed again after every one, so passing trouble has
 * already been given its chances. That flag waits for a person, which is
 * exactly what `flagClearsOnVerifiedIdentity` does with any reason that is not
 * the breaker's.
 */
export const HALT_LADDER_FLAG_REASON: AccountObservedStateReason = 'halt_ladder_exhausted'

/**
 * Whether a verified identity refresh may take this flag back on its own.
 *
 * TRUE FOR EXACTLY ONE CASE: an account the failure circuit breaker flagged.
 * The breaker trips on consecutive failures, and the commonest cause of ten
 * failures in a row is a handset nobody can reach. The engine re-verifying the
 * account's identity on that handset proves both halves of what was false when
 * the breaker tripped, at once: the device answers, and the right account is
 * signed in on it. So the condition that raised the flag is measurably over.
 *
 * FALSE FOR EVERYTHING ELSE, and that is the load-bearing half. A platform
 * restriction and an app somebody signed out of are not healed by a phone
 * becoming reachable. Clearing those automatically would put a restricted
 * identity back in front of people because its battery was charged, which is a
 * worse failure than the deadlock this fixes. An unknown reason is treated the
 * same way and waits for a person.
 */
export function flagClearsOnVerifiedIdentity(
  observedState: AccountObservedState,
  observedStateReason: string | null,
): boolean {
  return observedState === 'flagged' && observedStateReason === BREAKER_FLAG_REASON
}

/** Effective health = `disabled` when admin_state is disabled, else observed_state (G4). */
export const EFFECTIVE_ACCOUNT_HEALTHS = ['disabled', 'warming', 'healthy', 'throttled', 'flagged'] as const
export type EffectiveAccountHealth = (typeof EFFECTIVE_ACCOUNT_HEALTHS)[number]

export function effectiveAccountHealth(
  adminState: AccountAdminState,
  observedState: AccountObservedState,
): EffectiveAccountHealth {
  return adminState === 'disabled' ? 'disabled' : observedState
}

/** G4 — `reserve()` denies unless effective health is healthy or warming (warming applies ramp caps). */
export function healthAllowsReservation(health: EffectiveAccountHealth): boolean {
  return health === 'healthy' || health === 'warming'
}

/**
 * G4 — the engine may only degrade health. Ordered worst-last so a transition is
 * legal for the engine iff it moves strictly toward `flagged`.
 */
const OBSERVED_STATE_SEVERITY: Record<AccountObservedState, number> = {
  warming: 0,
  healthy: 1,
  throttled: 2,
  flagged: 3,
}

/** True when `to` is strictly more severe than `from` — the only engine-legal transition (G4). */
export function isEngineLegalHealthTransition(
  from: AccountObservedState,
  to: AccountObservedState,
): boolean {
  return OBSERVED_STATE_SEVERITY[to] > OBSERVED_STATE_SEVERITY[from]
}

// ---------------------------------------------------------------------------
// Manual work queue (§5.M7, G7, G9, G13)
// ---------------------------------------------------------------------------

/** A card is either a resolved outreach action or one of the two housekeeping sweeps. */
export const CARD_KINDS = ['action', 'inbox_check', 'connections_check'] as const
export type CardKind = (typeof CARD_KINDS)[number]
export const cardKindSchema = enumOf(CARD_KINDS)

export const CARD_STATES = ['open', 'done', 'skipped', 'replied', 'expired', 'revoked'] as const
export type CardState = (typeof CARD_STATES)[number]
export const cardStateSchema = enumOf(CARD_STATES)

/** The four dispositions a human can apply to a card (§5.M7, G9). */
export const CARD_DISPOSITIONS = ['done', 'skip', 'replied', 'flag'] as const
export type CardDisposition = (typeof CARD_DISPOSITIONS)[number]
export const cardDispositionSchema = enumOf(CARD_DISPOSITIONS)

export function isTerminalCardState(state: CardState): boolean {
  return state !== 'open'
}

// ---------------------------------------------------------------------------
// Enrollments (G17) & methods (G18)
// ---------------------------------------------------------------------------

/** G17 — the enrollment state machine's full state set. */
export const ENROLLMENT_STATES = [
  'active',
  'waiting',
  'paused_reply',
  'paused_approval',
  'paused_manual',
  'snoozed',
  'completed',
  'disqualified',
] as const
export type EnrollmentState = (typeof ENROLLMENT_STATES)[number]
export const enrollmentStateSchema = enumOf(ENROLLMENT_STATES)

/** G17 — the scheduler scans `active` and `waiting` rows only. */
export const SCHEDULABLE_ENROLLMENT_STATES = ['active', 'waiting'] as const
export type SchedulableEnrollmentState = (typeof SCHEDULABLE_ENROLLMENT_STATES)[number]

export function isSchedulableEnrollmentState(state: EnrollmentState): state is SchedulableEnrollmentState {
  return state === 'active' || state === 'waiting'
}

export const TERMINAL_ENROLLMENT_STATES = ['completed', 'disqualified'] as const
export type TerminalEnrollmentState = (typeof TERMINAL_ENROLLMENT_STATES)[number]

export function isTerminalEnrollmentState(state: EnrollmentState): state is TerminalEnrollmentState {
  return state === 'completed' || state === 'disqualified'
}

/**
 * The states in which work this enrollment already handed over may still be
 * performed. Read at the moment a device claims a reservation, or a manual card
 * is marked done, which is the last point anything can be stopped.
 *
 * `completed` IS ON THIS LIST, and that is the whole point of the list existing.
 * A device step is marked executed when it is DISPATCHED and the enrollment then
 * advances, so on the final step there is nowhere left to go and the enrollment
 * completes while its own last reservation is still with the phone. Leaving
 * `completed` off meant the claim a minute later was refused, the reservation
 * was released `enrollment_paused`, and the last message of every sequence never
 * left the handset while the step still read `executed`. Measured 2026-09-16 on
 * both people in one campaign, on an X DM: dispatched, completed, refused,
 * nothing sent, and the campaign reporting 100% done.
 *
 * `completed` means the sequence reached its end, which is not a reason to
 * cancel the message it just handed over. Everything that IS such a reason stays
 * off the list and still stops the send:
 *
 *   `paused_reply`      they answered, so the next message must not go
 *   `paused_manual`     somebody stopped it by hand    (on the list: a person
 *   `paused_approval`   it is waiting on a human        chose to hold the walk,
 *                                                       not to cancel this send)
 *   `disqualified`      they were taken out of the campaign
 *   `snoozed`           the walk is deliberately asleep
 *
 * A reservation on a completed enrollment is bounded anyway: an unclaimed hold
 * is released by the hold sweeper once `hold_expires_at` passes, so this cannot
 * leave a send claimable indefinitely.
 *
 * ONE DEFINITION, because there were two. `routes/engine.ts` and `routes/cards.ts`
 * each declared their own copy with identical contents and no shared constant,
 * which is two places to remember and one to forget.
 */
export const CLAIMABLE_ENROLLMENT_STATES = ['active', 'waiting', 'paused_manual', 'paused_approval', 'completed'] as const
export type ClaimableEnrollmentState = (typeof CLAIMABLE_ENROLLMENT_STATES)[number]

export function isClaimableEnrollmentState(state: string): state is ClaimableEnrollmentState {
  return (CLAIMABLE_ENROLLMENT_STATES as readonly string[]).includes(state)
}

/**
 * G18: the Method DSL branch conditions.
 *
 * `not_emailed` joined on 2026-09-13 and it is the first member answered from
 * the enrollment's own history rather than from a fact about the person. It
 * holds when THIS enrollment has not sent an email yet, whatever other campaigns
 * have done, because the question the operator asked was about one sequence:
 * "if we have not emailed them already" in the workflow he was drawing. The
 * scheduler answers it from the enrollment's executed steps, so no condition
 * port is asked and no other brand or campaign can change the answer.
 *
 * `not_messaged` joined on 2026-09-14 and it is the general form of that one.
 * The owner's next question was "is there a way to check if an email has been
 * sent to the person already or not? this actually applies to all dm actions",
 * and the meeting before it wanted people not re-approached by a brand that had
 * already written to them. So the check takes a channel (one app, or any) and a
 * scope (this campaign, or anyone at the brand). See `MESSAGED_CHANNELS` and
 * `MESSAGED_SCOPES` for both, and `resolveMessagedCheck` for how a stored
 * `not_emailed` reads as one of these without its row being rewritten.
 *
 * `not_commented` joined on 2026-09-25 for the LinkedIn visit the owner
 * described: comment on one post when a post suits, and when none does, like
 * their posts instead. It holds when a comment step of THIS enrollment visited
 * the person and found nothing worth a comment, which the phone reports as a
 * declined step rather than a failed one. Answered from the enrollment's own
 * steps like `not_emailed`, and held for the phone like `connected`, because
 * the answer is the result of the comment in front of it.
 *
 * `has_signal`, `field_matches` and `icp_score_gte` joined on 2026-09-25, from
 * Conor's list of signals a sequence should be able to branch on. They are the
 * first checks that take a value the author chooses rather than a fixed word:
 * which signal and how recent, which field and what it has to say, and how high
 * the fit score has to be. They read what is already on file about the person
 * and nothing new is collected for them. See `prospect-checks.ts` for the keys,
 * the bounds on them and the one rule every reader answers them with.
 *
 * `attended_workshop`, `no_show`, `posts_active` and `went_quiet` joined on
 * 2026-09-26 from the same list. The first two read what the Luma import wrote
 * about the event a person registered for, the last two when a scan last saw
 * them do something, with the number of days as the author's choice. The same
 * module answers them.
 *
 * `not_sent` joined on 2026-09-27 for the company step: a sequence that
 * messages the person and falls back to their company. It holds when the latest
 * message this enrollment tried to send to the PERSON could not be sent: the
 * platform refused it, it had nobody to reach, or the phone reported it failed.
 * Answered from the enrollment's own steps and held for the phone like
 * `not_commented`, because the answer is the report of the message in front of
 * it. A message to their company never counts, so the check cannot answer
 * about the fallback it exists to trigger.
 *
 * `lead_magnet_opened`, `lead_magnet_clicked`, `lead_magnet_downloaded` and
 * `lead_magnet_opted_in` joined on 2026-09-27, from the owner's ask for "IF
 * sequences based on how a user interacts with the leadmagnet". They read the
 * events the signed link recorded for THIS enrollment (0135), so another
 * campaign's lead magnet never answers for this one. Clicked holds for anything
 * past the click, and opened for a download too. See `eventsThatSatisfy`.
 */
export const DSL_CONDITIONS = [
  'connected',
  'not_connected',
  'no_reply',
  'replied',
  'email_valid',
  'not_emailed',
  'not_messaged',
  'not_commented',
  'has_signal',
  'field_matches',
  'icp_score_gte',
  'attended_workshop',
  'no_show',
  'posts_active',
  'went_quiet',
  'not_sent',
  'lead_magnet_opened',
  'lead_magnet_clicked',
  'lead_magnet_downloaded',
  'lead_magnet_opted_in',
] as const
export type DslCondition = (typeof DSL_CONDITIONS)[number]
export const dslConditionSchema = enumOf(DSL_CONDITIONS)

/**
 * Which channel a "have we messaged them yet" check asks about.
 *
 * The eight apps a person can be sent words on, plus `any`. Not derived from
 * `ENABLED_CHANNELS`, because that list carries `messenger` beside `facebook`,
 * and those two are one inbox: the Facebook app's Message button opens
 * Messenger's own chat on the same thread (measured 2026-09-12, see the
 * `facebook` row of `CHANNEL_ACTION_TYPES`). Offering them as two choices would
 * let a check for Facebook say "not messaged" to somebody a Messenger account
 * wrote to minutes ago, in the very thread the next message would land in. So
 * `facebook` here means the family, and `messagedChannelFamily` says which
 * stored channels that is.
 */
export const MESSAGED_CHANNELS = ['email', 'linkedin', 'instagram', 'whatsapp', 'x', 'facebook', 'telegram', 'bluesky', 'any'] as const
export type MessagedChannel = (typeof MESSAGED_CHANNELS)[number]

/**
 * Whose messages a "have we messaged them yet" check counts.
 *
 * `sequence` is this enrollment's own steps, which is what `not_emailed` has
 * always read. `brand` is anything anybody at the brand sent the person: other
 * campaigns, an operator's one-off action, a card done by hand, and a message
 * logged or mirrored into the inbox. Never another brand's, because a brand not
 * knowing what a different brand said is the tenancy rule, not a gap in the check.
 */
export const MESSAGED_SCOPES = ['sequence', 'brand'] as const
export type MessagedScope = (typeof MESSAGED_SCOPES)[number]

/** What an omitted key means. Brand and any, because re-approaching somebody is the thing the check exists to stop. */
export const DEFAULT_MESSAGED_CHECK: { channel: MessagedChannel; scope: MessagedScope } = { channel: 'any', scope: 'brand' }

/**
 * The verbs that put a message in front of a person, for this check only.
 *
 * Narrower than `COPY_BEARING_ACTIONS` on purpose. A connection request can
 * carry a note and a comment carries words, and neither is a message in the
 * sense the owner asked about: a note arrives as an invitation somebody may
 * never open and a comment sits under a post. Counting them would make one
 * polite invitation stop every later message, which is the opposite of what a
 * sequence that connects first and messages second needs.
 */
export const MESSAGE_ACTIONS: readonly ActionType[] = ['send', 'dm', 'inmail']

export function isMessageAction(action: string | null | undefined): boolean {
  return action != null && (MESSAGE_ACTIONS as readonly string[]).includes(action)
}

export function isMessagedChannel(value: unknown): value is MessagedChannel {
  return typeof value === 'string' && (MESSAGED_CHANNELS as readonly string[]).includes(value)
}

export function isMessagedScope(value: unknown): value is MessagedScope {
  return typeof value === 'string' && (MESSAGED_SCOPES as readonly string[]).includes(value)
}

/**
 * The stored channels a check's channel covers, or null for `any`.
 *
 * `facebook` covers `messenger` as well. See `MESSAGED_CHANNELS` for why.
 */
export function messagedChannelFamily(channel: MessagedChannel): readonly Channel[] | null {
  if (channel === 'any') return null
  if (channel === 'facebook') return ['facebook', 'messenger']
  return [channel]
}

/** Whether a message sent on `sent` answers a check asking about `asked`. */
export function messagedChannelCovers(asked: MessagedChannel, sent: string): boolean {
  const family = messagedChannelFamily(asked)
  return family === null || (family as readonly string[]).includes(sent)
}

/**
 * What a condition asks about messages, or null when it is not that kind of check.
 *
 * `not_emailed` is read as `not_messaged` on email, in this campaign, and it is
 * read that way here rather than rewritten in the stored rows. Workflows saved
 * before 2026-09-14 carry it, and a migration that changed their documents would
 * make every one of them look edited to the differ that refuses structural edits
 * on a running campaign.
 *
 * An omitted key takes `DEFAULT_MESSAGED_CHECK`. A key that is not one of the
 * words also takes the default here, because the save has already refused it
 * with a sentence by the time anything asks, and a reader on the browser side
 * would rather show the default than throw.
 */
export function resolveMessagedCheck(
  cond: string | null | undefined,
  keys: { channel?: unknown; scope?: unknown } = {},
): { channel: MessagedChannel; scope: MessagedScope } | null {
  if (cond === 'not_emailed') return { channel: 'email', scope: 'sequence' }
  if (cond !== 'not_messaged') return null
  return {
    channel: isMessagedChannel(keys.channel) ? keys.channel : DEFAULT_MESSAGED_CHECK.channel,
    scope: isMessagedScope(keys.scope) ? keys.scope : DEFAULT_MESSAGED_CHECK.scope,
  }
}

/** §4 — per-step approval gate (copilot autonomy). */
export const APPROVAL_STATES = ['n/a', 'pending', 'approved', 'rejected'] as const
export type ApprovalState = (typeof APPROVAL_STATES)[number]
export const approvalStateSchema = enumOf(APPROVAL_STATES)

/** §4 — `campaigns.autonomy`. Copilot (approve-first) is the default everywhere (§9). */
export const AUTONOMIES = ['copilot', 'autopilot'] as const
export type Autonomy = (typeof AUTONOMIES)[number]

/**
 * The reading vocabulary. Both members, and both stay.
 *
 * Autopilot is locked for writing (see `openAutonomy` in `policy.ts`), and the
 * cheap-looking way to do that would have been to drop the member from this
 * list. It would also have been wrong: a campaign is running on autopilot right
 * now, and a stored row this schema cannot parse is a campaign that cannot be
 * listed, opened, explained or paused. Locking a door is not the same as
 * deleting the word for what is behind it.
 */
export const autonomySchema = enumOf(AUTONOMIES)

/**
 * An autonomy value that arrived in a request body and has not been past the
 * autopilot lock yet.
 *
 * **It is an object rather than a string on purpose, and that is the whole
 * mechanism.** Every store that persists this field takes a plain `string`, so
 * wrapping it means `autonomy: body.autonomy` stops compiling at every write
 * path, and the only way back to a string is `openAutonomy`, which reads the
 * lock before it hands one over.
 *
 * The alternative was a rule enforced by review. There were five places that
 * wrote this field and not one shared line of code between them, so a rule like
 * that survives exactly until the sixth route is written by somebody who never
 * read this comment. Now forgetting it is a type error rather than a campaign
 * that sends unattended.
 */
export interface RequestedAutonomy {
  readonly requested: Autonomy
}

/**
 * The writing vocabulary: same two words, wrapped so they cannot be spent
 * without passing the lock. Every request body that sets `campaigns.autonomy`
 * parses through this one, including the public REST API, which used to carry
 * its own hand-written copy of the enum and would have missed any rule added
 * here.
 */
export const autonomyWriteSchema = autonomySchema.transform(
  (requested): RequestedAutonomy => ({ requested }),
)

// ---------------------------------------------------------------------------
// Suppression & identity (§5.M3, G14, G19)
// ---------------------------------------------------------------------------

/**
 * §4 `suppressions.reason_code` + `CROSS_BRAND_ACTIVE` (G14) +
 * `ALREADY_IN_SEQUENCE`.
 *
 * The last two are *blocks*, not permanent suppressions: both clear when the
 * contact that caused them ends or its cooldown lapses (see
 * `isPermanentSuppression`).
 *
 * `ALREADY_IN_SEQUENCE` is the same-brand twin of `CROSS_BRAND_ACTIVE`, and it
 * is a separate code rather than a widening of that one because the two mean
 * different things to whoever reads them: cross-brand is "another part of the
 * business owns this relationship", same-brand is "a colleague is already in
 * this inbox, or you have this person on your list twice". The second one is
 * usually a data problem the operator can fix, and folding them together would
 * hide that.
 */
export const SUPPRESSION_REASONS = [
  'PRIOR_CONVERSATION',
  'EXISTING_USER',
  'HOSTILE_REPLY',
  'UNSUBSCRIBE',
  'MANUAL',
  'CRM_CUSTOMER',
  'BOUNCED',
  'CROSS_BRAND_ACTIVE',
  'ALREADY_IN_SEQUENCE',
  // One of our own people or accounts (0133). Never stored as a suppression
  // row: the check reads the protected list itself, every time.
  'OUR_OWN_ACCOUNT',
] as const
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number]
export const suppressionReasonSchema = enumOf(SUPPRESSION_REASONS)

/**
 * A permanent suppression stands until somebody removes it.
 *
 * The two collision blocks are the exceptions: both are statements about what is
 * happening right now, and both lift on their own when it stops. Treating either
 * as permanent would retire a prospect for good over a week of overlap.
 */
const TEMPORARY_SUPPRESSIONS: readonly SuppressionReason[] = ['CROSS_BRAND_ACTIVE', 'ALREADY_IN_SEQUENCE']

export function isPermanentSuppression(reason: SuppressionReason): boolean {
  return !TEMPORARY_SUPPRESSIONS.includes(reason)
}

/** §4 — suppression scope. `global` entries are honoured across every brand (§7). */
export const BRAND_SCOPES = ['global', 'brand'] as const
export type BrandScope = (typeof BRAND_SCOPES)[number]
export const brandScopeSchema = enumOf(BRAND_SCOPES)

/** Identity key types used by `prospect_identities`, `suppressions` and `conversation_index`. */
export const ID_TYPES = ['email', 'linkedin_url', 'domain', 'phone', 'name_company', 'mongo_user_id'] as const
export type IdType = (typeof ID_TYPES)[number]
export const idTypeSchema = enumOf(ID_TYPES)

/**
 * A social handle as an identity key, one type per network (0118).
 *
 * Admitted by the `id_type` constraints on `prospect_identities`, `suppressions`
 * and `conversation_index`, and accepted as an ingested participant, so an
 * engine that can read a handle files a conversation under it. The value is the
 * handle bare and lowercase, with no leading `@`. Messenger has none of its own:
 * it shares Facebook's identity.
 */
export const HANDLE_ID_TYPES = ['instagram_handle', 'x_handle', 'facebook_handle', 'telegram_handle', 'tiktok_handle'] as const
export type HandleIdType = (typeof HANDLE_ID_TYPES)[number]

// ---------------------------------------------------------------------------
// Prospects, signals & observations (§5.M4, G5)
// ---------------------------------------------------------------------------

export const PROSPECT_STATUSES = [
  'new',
  'qualified',
  'enrolled',
  'paused',
  'replied',
  'disqualified',
  'suppressed',
  'converted',
] as const
export type ProspectStatus = (typeof PROSPECT_STATUSES)[number]
export const prospectStatusSchema = enumOf(PROSPECT_STATUSES)

/** §5.M4 — the feasible v1 signal set. The full catalog is v2 behind the same schema. */
export const SIGNAL_TYPES = [
  'list_import',
  'job_change',
  'funding_round',
  'manual_tag',
  'post_engagement',
] as const
export type SignalType = (typeof SIGNAL_TYPES)[number]
export const signalTypeSchema = enumOf(SIGNAL_TYPES)

/**
 * Signals that say how somebody arrived, rather than anything about them.
 *
 * `list_import` is on every person who ever came from a file, which makes it
 * the most common signal on the platform and the least sayable: "I saw list
 * import on 18 August" is a sentence about our own filing, addressed to a
 * stranger.
 *
 * The distinction exists because the `{{signal}}` merge variable is offered to
 * the copy generator on the strength of its coverage, and coverage was counting
 * *any* signal. Every imported list therefore read as "100% of these people have
 * a signal worth opening with", and the generator obligingly opened with one.
 *
 * `manual_tag` is deliberately NOT here. Somebody attached it on purpose and its
 * text is theirs, so it can be worth saying. Provenance is the thing nobody
 * chose.
 */
export const PROVENANCE_SIGNAL_TYPES: readonly SignalType[] = ['list_import']

/** Is this a signal worth building a sentence on, rather than filing? */
export function isSayableSignal(type: SignalType): boolean {
  return !PROVENANCE_SIGNAL_TYPES.includes(type)
}

/**
 * G5 — `prospect_observations.obs_type`. Latest per (prospect, account, type) wins.
 *
 * `channel_presence` carries `{ present: boolean }`: does the channel's own
 * app find an account at this person's address. Written by the engine result
 * path when the app itself answers that no account exists there (WhatsApp's
 * "isn't on WhatsApp" dialog, measured), and read by the dispatcher to refuse
 * re-dispatch on that channel account. Latest row wins like every other type,
 * so one newer `present: true` unblocks a person who has since joined.
 */
export const OBSERVATION_TYPES = [
  'connection_state',
  'degree',
  'last_activity',
  'profile_exists',
  'channel_presence',
  // What a public profile said, in the person's own words, the last time one was
  // read. The raw evidence an interest read is built from, kept separately from
  // the reading itself so that re-deriving interests never needs the phone again.
  'public_profile',
  // What a profile showed when the device read it on an ordinary visit: the
  // headline, the top-card lines in screen order, and the counts beside them
  // (followers, connections, degree), each exactly as the screen put it.
  //
  // NOT `public_profile`, and the difference is load-bearing. `public_profile`
  // is profile_scan's product, and its readers treat `postsAvailable` as a
  // fact about the person's posts: the opening router reads the newest one per
  // channel, both for what the posts say and for when that profile was last
  // read. A profile view never looks at posts, so a row of that type written
  // off one would tell the router this person has nothing to read and has just
  // been read, when nobody looked at the posts at all.
  'profile_details',
  // Whether LinkedIn offered a free message to this person without a
  // connection, read by the phone when it tapped Message (an Open Profile).
  // `{ open: true }` lets an account with no Premium seat write to them, and
  // a plain LinkedIn message to a non-connection goes as one of these instead
  // of being refused. See migration 0127.
  'open_profile',
  // The owner's Instagram visit (0136): the posts it looked at with their
  // captions and how many comments it read under each, the tabs it opened, whether
  // it saw their story, and the sites of the links it opened. Written off an
  // Instagram profile_view, read by the follow's fit, the message and
  // {{instagram_post}}.
  'instagram_visit',
] as const
export type ObservationType = (typeof OBSERVATION_TYPES)[number]
export const observationTypeSchema = enumOf(OBSERVATION_TYPES)

/** G5 — who wrote an observation. */
export const OBSERVATION_SOURCES = ['manual_card', 'manual_entry', 'engine', 'import'] as const
export type ObservationSource = (typeof OBSERVATION_SOURCES)[number]
export const observationSourceSchema = enumOf(OBSERVATION_SOURCES)

// ---------------------------------------------------------------------------
// Inbox & learning (§5.M8)
// ---------------------------------------------------------------------------

/** §5.M8 / §6.4 — reply classification label set. */
export const CLASSIFICATION_LABELS = [
  'HOSTILE',
  'NEGATIVE',
  'NEUTRAL',
  'QUESTION',
  'POSITIVE',
  'OOO',
  'WRONG_PERSON',
  'UNSUBSCRIBE',
] as const
export type ClassificationLabel = (typeof CLASSIFICATION_LABELS)[number]
export const classificationLabelSchema = enumOf(CLASSIFICATION_LABELS)

/** Labels that disqualify + suppress on sight (§5.M8 routing). */
export const DISQUALIFYING_LABELS = ['HOSTILE', 'UNSUBSCRIBE'] as const
export type DisqualifyingLabel = (typeof DISQUALIFYING_LABELS)[number]

export function isDisqualifyingLabel(label: ClassificationLabel): label is DisqualifyingLabel {
  return label === 'HOSTILE' || label === 'UNSUBSCRIBE'
}

/** Suppression reason implied by a disqualifying classification (§5.M8). */
export function suppressionReasonForLabel(label: DisqualifyingLabel): SuppressionReason {
  return label === 'HOSTILE' ? 'HOSTILE_REPLY' : 'UNSUBSCRIBE'
}

export const MESSAGE_DIRECTIONS = ['inbound', 'outbound'] as const
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number]
export const messageDirectionSchema = enumOf(MESSAGE_DIRECTIONS)

/**
 * The `messages.meta` key that says the time beside a message is our best
 * reading of it rather than something the app published.
 *
 * Measured on the handset: Messenger and X put no time on the message itself,
 * and Instagram, LinkedIn and WhatsApp publish a date separator above a run of
 * messages and nothing finer. So the engine either reads a real timestamp or it
 * places the message from what is around it, and only the engine can tell the
 * two apart. Without this flag the platform stores a Date either way and every
 * screen renders it with the same confidence, which turns a reading into a
 * claim.
 *
 * The key lives here, in the one package both sides depend on, because the
 * writer is `@gojibasel/integrations` and the reader is the API: two spellings
 * of the same string in two packages is a drift that no test on either side can
 * see.
 */
export const APPROXIMATE_SENT_AT_META_KEY = 'sentAtIsApproximate'

/**
 * The `messages.meta` key that says a DISPLAY NAME, not an identifier, decided
 * whose message this is. Its VALUE is the tier that answered: `platform_name`
 * when the app itself named this handle to us, `prospect_record` when the name
 * matched somebody the reading account had already acted on.
 *
 * Absent on every deterministic match, so absence reads as "an identifier said
 * so" rather than as "nobody recorded it". A key written on every message with
 * most of them saying "no inference" would bury the ones that ARE inferences,
 * which is the same argument the approximate-time flag above makes.
 *
 * HERE RATHER THAN IN THE PACKAGE THAT OWNS THE RULE, because two packages
 * write it now: `@gojibasel/inbox` on the sink path and
 * `@gojibasel/integrations` on the route path, and they may not import each
 * other. Two spellings of the same string is a drift no test on either side can
 * see, which is exactly what this key exists to make visible.
 */
export const MATCHED_BY_DISPLAY_NAME_META_KEY = 'matchedByDisplayName'

/**
 * Whether a stored message's `meta` marks its `sent_at` as approximate.
 *
 * Absent reads as exact, and that is the only honest default: rows written
 * before the flag existed, and sources that never had the question (an email
 * carries a Date header), both say nothing here, and treating silence as doubt
 * would put a caveat under every message in the product.
 */
export function hasApproximateSentAt(meta: unknown): boolean {
  if (meta === null || typeof meta !== 'object') return false
  return (meta as Record<string, unknown>)[APPROXIMATE_SENT_AT_META_KEY] === true
}

/**
 * The `messages.meta` key holding a message's position in its thread, 0 at the
 * top, as the engine read it off the screen.
 *
 * A second sort key, and it exists because the first one ties. A chat app draws
 * a time between bubbles rather than on them, so a device sweep stamps a whole
 * thread with the moment it read it and every row in that thread sorts equal. A
 * tie on the sort key is not a small inaccuracy, it is no order at all: the
 * reader gets whatever the database happened to return, and the Connor Dixon
 * thread rendered with its opening message last.
 *
 * WHAT IT IS NOT is a fixed address in the conversation. `read_thread` numbers
 * whatever that read walked, so a deep sweep and a shallow one number the same
 * messages differently. Ingestion stores the first arrival and refuses the rest
 * by external id, so the numbers stay self-consistent within one thread, and
 * they are not comparable across threads or across reads of different depth.
 *
 * Here rather than in `@gojibasel/integrations` for the same reason its sibling
 * above is here: it is written by ingestion, read by the API, and parsed by a
 * second copy of the wire schema in `@gojibasel/inbox`. Three packages, one
 * string, and a reader that spells it differently finds nothing and reports
 * nothing, which looks exactly like an engine that never sent it.
 */
export const MESSAGE_ORDINAL_META_KEY = 'ordinal'

/**
 * The `messages.meta` key that says the engine could not read who wrote a
 * message, so the `direction` stored beside it is the safer reading rather than
 * something observed.
 *
 * A sender label on a chat screen precedes a RUN of messages and is not
 * repeated, so the topmost visible message's label is often scrolled off. The
 * engine says inbound in that case, because in a transcript somebody reads to
 * decide what to say next, claiming they wrote something they did not is the
 * worse error. The flag is what makes that a reading rather than a second
 * silent guess.
 */
export const UNKNOWN_DIRECTION_META_KEY = 'directionIsUnknown'

/**
 * A stored message's position in its thread, or null when nothing said.
 *
 * Read strictly. A blob is whatever was written into it, so a string "3" and a
 * 3.5 are refused rather than coerced: a position on a screen is a whole count,
 * and a sort key that is sometimes a string sorts "10" before "2".
 */
export function messageOrdinalIn(meta: unknown): number | null {
  if (meta === null || typeof meta !== 'object') return null
  const value = (meta as Record<string, unknown>)[MESSAGE_ORDINAL_META_KEY]
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null
}

/**
 * Whether a stored message's meta says nobody read who wrote it.
 *
 * Absent reads as attributed, for the same reason absent reads as exact above.
 * Every row written before this field existed came off a path that never
 * reported doubt, so silence there is silence, and treating it as doubt would
 * put a hedge over every message in the product.
 */
export function hasUnknownDirection(meta: unknown): boolean {
  if (meta === null || typeof meta !== 'object') return false
  return (meta as Record<string, unknown>)[UNKNOWN_DIRECTION_META_KEY] === true
}

/**
 * The `messages.meta` key holding the subject this particular message carried.
 *
 * WHY IT IS IN THE BLOB AND NOT IN A COLUMN. `messages` has never had a subject
 * column. `conversations.subject` is written once, by whichever message opened
 * the thread, and every message after it had its subject parsed by an adapter
 * and then dropped on the floor: the Instantly webhook reads `reply_subject` on
 * every reply, the engine batch declares `subject` on every message, and
 * ingestion passed both to `createConversation` and to nothing else. The reader
 * on the API side compensated by hardcoding `subject: null` on the message wire,
 * so the thread pane's per-message subject branch could not fire on any message
 * in the product.
 *
 * A column would have been the other answer and it is a worse one here. This is
 * provenance the adapter already recorded, `meta` is exactly the place 0057 made
 * for provenance the adapter recorded and the insert had nowhere to put, and a
 * nullable column on the hottest table in the schema for a value that is null on
 * every device channel is a column that costs every row to serve email.
 *
 * The key lives here, in the one package both sides depend on, for the reason
 * its three siblings above do: the writer is `@gojibasel/inbox` and the reader is
 * the API, and one string spelled two ways is a drift no test on either side can
 * see.
 */
export const MESSAGE_SUBJECT_META_KEY = 'subject'

/**
 * A stored message's own subject, or null when nothing recorded one.
 *
 * Read strictly, and trimmed-empty reads as null, because a subject of spaces
 * would render as a blank header line above the body with no way to tell it from
 * a rendering bug.
 *
 * ABSENT IS NOT EVIDENCE OF ABSENCE, and this is the one of the four helpers
 * here where that matters most. Every message stored before this key existed
 * reads null whatever its source parsed, and on email that is a real subject the
 * platform threw away rather than a thread that had none. The thread's own
 * subject is still on the conversation, which is what the pane draws as its
 * heading, so a null here costs the reader nothing they did not already have.
 */
export function messageSubjectIn(meta: unknown): string | null {
  if (meta === null || typeof meta !== 'object') return null
  const value = (meta as Record<string, unknown>)[MESSAGE_SUBJECT_META_KEY]
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length === 0 ? null : trimmed
}

/** §4 — bandit arm families (learning loop, I.6). */
export const ARM_TYPES = ['asset', 'method', 'copy_variant', 'signal_type'] as const
export type ArmType = (typeof ARM_TYPES)[number]
export const armTypeSchema = enumOf(ARM_TYPES)

export const OUTCOME_TYPES = [
  'positive_reply',
  'neutral_reply',
  'hostile_reply',
  'no_reply',
  'meeting',
  'converted',
] as const
export type OutcomeType = (typeof OUTCOME_TYPES)[number]
export const outcomeTypeSchema = enumOf(OUTCOME_TYPES)

// ---------------------------------------------------------------------------
// Content, tenancy & policy scopes
// ---------------------------------------------------------------------------

/** §12.6 — asset taxonomy. */
export const ASSET_TYPES = [
  'case_study',
  'industry_prototype',
  'transformation_results',
  'path_report',
  'other',
] as const
export type AssetType = (typeof ASSET_TYPES)[number]
export const assetTypeSchema = enumOf(ASSET_TYPES)

export const USER_ROLES = ['admin', 'member'] as const
export type UserRole = (typeof USER_ROLES)[number]
export const userRoleSchema = enumOf(USER_ROLES)

/** §4 `quota_policies.scope`. Person always wins (§5.M2). */
export const POLICY_SCOPES = ['person', 'account', 'brand', 'campaign'] as const
export type PolicyScope = (typeof POLICY_SCOPES)[number]
export const policyScopeSchema = enumOf(POLICY_SCOPES)

/** Scope set for non-quota safety settings (`safety_settings`). */
export const SAFETY_SCOPES = ['global', 'brand', 'account', 'device', 'campaign'] as const
export type SafetyScope = (typeof SAFETY_SCOPES)[number]
export const safetyScopeSchema = enumOf(SAFETY_SCOPES)

/** G8 — LinkedIn data-export parse modes. */
export const EXPORT_PARSE_MODES = ['index_only', 'full_thread'] as const
export type ExportParseMode = (typeof EXPORT_PARSE_MODES)[number]
export const exportParseModeSchema = enumOf(EXPORT_PARSE_MODES)

/** G16 — mailbox roles. `team` mailboxes are headers-only, never bodies. */
export const MAILBOX_KINDS = ['outreach', 'team'] as const
export type MailboxKind = (typeof MAILBOX_KINDS)[number]
export const mailboxKindSchema = enumOf(MAILBOX_KINDS)

// ---------------------------------------------------------------------------
// Cross-cutting value objects
// ---------------------------------------------------------------------------

/**
 * Brand scoping context. Repository methods require one of these (§5.M1) so a
 * missing brand predicate is a compile error rather than a data leak.
 */
export interface BrandContext {
  readonly brandId: string
  readonly userId: string
  readonly role: UserRole
}

/** The three sanctioned cross-brand readers (§5.M1 + G9). Anything else must scope by brand. */
export const CROSS_BRAND_READERS = ['quota_governor', 'global_suppression', 'master_view', 'manual_queue'] as const
export type CrossBrandReader = (typeof CROSS_BRAND_READERS)[number]

/** ISO weekday, 1 = Monday … 7 = Sunday (matches the ISO week keys in `time.ts`). */
export type IsoWeekday = 1 | 2 | 3 | 4 | 5 | 6 | 7
export const isoWeekdaySchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal(6),
  z.literal(7),
])

/** `HH:MM` in 24-hour local wall-clock time. */
export const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM (24h)')
export type LocalTime = z.infer<typeof localTimeSchema>

/** One working-hours window: weekdays + a local `[start, end)` interval (§5.M2). */
export const workingHoursWindowSchema = z
  .object({
    days: z.array(isoWeekdaySchema).min(1),
    start: localTimeSchema,
    end: localTimeSchema,
  })
  .refine((w) => w.start < w.end, { message: '`start` must be earlier than `end` (windows may not cross midnight)' })
export type WorkingHoursWindow = z.infer<typeof workingHoursWindowSchema>

/**
 * `campaigns.working_hours`.
 * - `null`/`undefined` — unrestricted (any time is a working time).
 * - `[]`               — nothing is ever a working time (explicitly closed).
 */
export const workingHoursSchema = z.array(workingHoursWindowSchema)
export type WorkingHours = WorkingHoursWindow[]

/**
 * When a new campaign sends, unless somebody says otherwise.
 *
 * Campaigns used to be created with no window at all, which reads as
 * "unrestricted" and means a step that comes due at 04:00 goes at 04:00. That
 * is fine for a card somebody picks up in the morning and wrong for an email,
 * which lands with a timestamp on it: a message that arrived at four in the
 * morning says "this was sent by a machine" more loudly than anything in the
 * copy.
 *
 * Noon to half five, weekdays, chosen on 2026-08-18 by the operator. The start
 * is the part that was asked for and the end is the house window's own. The
 * hours are read in the campaign's timezone, which the console sets from the
 * person creating it, so "noon" means noon where they are.
 *
 * A campaign may still carry any window, or none. This is the value a new one
 * starts with rather than a rule about what a window may be.
 */
export const DEFAULT_CAMPAIGN_WORKING_HOURS: WorkingHours = [
  { days: [1, 2, 3, 4, 5], start: '12:00', end: '17:30' },
]

/** A UUID string. */
export const uuidSchema = z.string().uuid()

/** Anything the audit log or a job payload may carry as free-form JSON. */
export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }
export type JsonObject = { [key: string]: JsonValue }
